import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import { Button } from '../../components/ui/button';
import BreadcrumbViewer from '../../components/BreadcrumbViewer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import InviteTalentModal from '../../components/InviteTalentModal';
import RecipientsModal from '../../components/RecipientsModal';
import LiveRateCalculator from '../../components/LiveRateCalculator';
import AssignmentBadge from '../../components/AssignmentBadge';
import ShiftAttachments from '../../components/ShiftAttachments';
import AttendanceCompliancePanel from '../../components/AttendanceCompliancePanel';
import ClockCorrectionControls from '../../components/ClockCorrectionControls';
import ShiftEventCodeCard from '../../components/ShiftEventCodeCard';
import PostEventSubmissionsPanel from '../../components/PostEventSubmissionsPanel';
import ShiftTaskMonitor from '../../components/tasks/ShiftTaskMonitor';
import CoveragePanel from '../../components/shift/CoveragePanel';
import OnsiteArrivalCard from '../../components/shift/OnsiteArrivalCard';
import ApplicantDrawer, { DrawerInlineError } from '../../components/ApplicantDrawer';
import DressCodeGroomingPicker from '../../components/DressCodeGroomingPicker';
import ErrorBoundary from '../../components/ErrorBoundary';
import LinkifiedText from '../../components/LinkifiedText';
import SendShiftReminderModal from '../../components/SendShiftReminderModal';
import SendEventEmailModal from '../../components/SendEventEmailModal';
import EventTitleField from '../../components/EventTitleField';
import EventScheduleEditor from '../../components/EventScheduleEditor';
import RecentInviteActivityCard from '../../components/RecentInviteActivityCard';
import PreviousWorkersSection from '../../components/PreviousWorkersSection';
import MarketBadge from '../../components/MarketBadge';
import PlatformBalanceBanner from '../../components/PlatformBalanceBanner';  // P0.16 follow-up
import EventRecapAiCard from '../../components/admin/EventRecapAiCard';  // PO-2026-06 #7

import PayoutTermsCard from '../../components/admin/PayoutTermsCard';
import {
  ArrowLeft, MapPin, Calendar, Clock, Users, DollarSign,
  Search, Loader2, UserPlus, CheckCircle, CheckCircle2, Save, Send, Copy, FileText, Paperclip, Trash2,
  Inbox, Banknote, AlertTriangle, MapPinned, UserMinus, UserX, Zap, RefreshCw, Shield, Bell, MoreHorizontal,
  Camera, Star, User as UserIcon, Timer, Flag, ArrowRightLeft, Smartphone, ClipboardList, Sparkles, Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import FlagViolationModal from '../../components/admin/FlagViolationModal';
import TierRoleCombobox from '../../components/TierRoleCombobox';
import { formatDateMMDDYYYY, formatDateWithWeekday, formatTimeRange, timezoneLabel, formatClockTimeInEventTz } from '../../utils/timeFormat';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import FindReplacementsDialog from '../../components/FindReplacementsDialog';
import StatusChip from '../../components/StatusChip';
import BookingStatusBadge from '../../components/BookingStatusBadge';
import LifecycleTimeline from '../../components/LifecycleTimeline';
import NotPayableBadge from '../../components/NotPayableBadge';
import UrgencyChips from '../../components/UrgencyChips';
import BadgeRow from '../../components/BadgeRow';
import { useTalentProfileModal } from '../../components/TalentProfileModalProvider';
import { useInviteCart } from '../../components/InviteCartProvider';
import { Switch } from '../../components/ui/switch';

const statusColors = {
  open: 'bg-amber-100 text-amber-700',
  confirmed: 'bg-green-100 text-green-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

const AdminShiftDetail = () => {
  const { shiftId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isSuperAdmin } = useAuth();
  const superAdmin = isSuperAdmin && isSuperAdmin();
  const { open: openProfile } = useTalentProfileModal();
  // Iter-193 — destructure bindContextShift/unbindContextShift used by
  // the useEffect below (lost during an earlier merge; restored iter-200).
  const { bindContextShift, unbindContextShift } = useInviteCart();
  const [loading, setLoading] = useState(true);
  const [flagTarget, setFlagTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [eventScheduleOpen, setEventScheduleOpen] = useState(false);
  const [edits, setEdits] = useState({});
  // Iter-234h — curated dress-code / grooming presets (loaded on mount
  // so the Edit panel can show the same picker the Create form uses).
  const [curatedDressCodes, setCuratedDressCodes] = useState([]);
  const [curatedAttireRestrictions, setCuratedAttireRestrictions] = useState([]);
  const [curatedGroomingReqs, setCuratedGroomingReqs] = useState([]);

  // Talent search for manual assign
  const [talentQuery, setTalentQuery] = useState('');
  const [talentResults, setTalentResults] = useState([]);
  const [talentSearching, setTalentSearching] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteNearbyRadius, setInviteNearbyRadius] = useState(null);
  const [recapReq, setRecapReq] = useState(null); // {shift_photo_min, effective_*, required_shots}
  const [recapMin, setRecapMin] = useState('');
  const [recapShots, setRecapShots] = useState('');
  const [recapSaving, setRecapSaving] = useState(false);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [payingBookingId, setPayingBookingId] = useState(null);
  const [locBookingId, setLocBookingId] = useState(null);
  // Iter-133w — promote-backup-to-primary flow
  const [promoteTarget, setPromoteTarget] = useState(null);   // { booking_id, name }
  const [timeEdit, setTimeEdit] = useState(null);  // { booking_id, name, start_time, end_time, note }
  const [savingTime, setSavingTime] = useState(false);
  const [promoteReason, setPromoteReason] = useState('Primary was a no-show — backup worked the shift');
  const [promoting, setPromoting] = useState(false);

  const downloadSignInSheet = async () => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/attendance/pdf/${shiftId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sign-in-sheet_${shiftId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sign-in sheet downloaded');
    } catch (e) {
      toast.error(e.message || 'Failed to download sign-in sheet');
    }
  };

  const payTalent = async (t) => {
    const amountLabel = t.final_pay ? ` ($${Number(t.final_pay).toFixed(2)})` : '';
    if (!window.confirm(`Pay ${t.name || t.email}${amountLabel} via Stripe Connect? This cannot be undone once Stripe confirms the transfer.`)) return;
    setPayingBookingId(t.booking_id);
    try {
      const res = await api.post(`/admin/bookings/${t.booking_id}/payout`, {});
      toast.success(`Payout initiated: $${res.data.amount} → ${res.data.talent}`);
      load();
    } catch (e) {
      // Backend now returns either a string or a structured {code,message,retryable}
      // for Stripe failures. Coerce to a human-readable string so admins never see
      // a blank "nothing happened" — that was the P0 Anita-Currie regression.
      const d = e.response?.data?.detail;
      const httpTxt = e.response?.status ? ` (HTTP ${e.response.status})` : '';
      const msg = typeof d === 'string'
        ? d
        : (d && (d.message || d.code)) || e.message || 'Payout failed';
      toast.error(`${msg}${httpTxt}`);
    } finally {
      setPayingBookingId(null);
    }
  };
  const [cloning, setCloning] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState('');
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [applicantDrawerOpen, setApplicantDrawerOpen] = useState(false);

  // Iter-132: Mark Shift Completed — closes primary bookings in bulk,
  // recomputes hours_worked + total_pay from clock times, stamps approved_*,
  // and flips shift.status → 'completed'. Backend audits each transition.
  const handleMarkComplete = async () => {
    if (!window.confirm(
      'Mark this shift as completed?\n\n' +
      'This will close every primary booking that has both clock-in and clock-out ' +
      'times, recompute hours and pay, and mark the shift as completed.\n\n' +
      'Bookings missing clock times, backups, cancelled, and no-show rows will be skipped.'
    )) return;
    setMarkingComplete(true);
    try {
      const res = await api.post(`/admin/shifts/${shiftId}/mark-complete`);
      const { completed_count, skipped_count } = res.data;
      if (completed_count > 0) {
        toast.success(`Marked complete — ${completed_count} booking${completed_count === 1 ? '' : 's'} closed, ${skipped_count} skipped`);
      } else {
        toast.warning(`Nothing to complete — ${skipped_count} booking${skipped_count === 1 ? '' : 's'} skipped (missing clock times or already closed)`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to mark shift complete');
    } finally {
      setMarkingComplete(false);
    }
  };

  const handleSoftDelete = async () => {
    setDeleting(true);
    try {
      // Iter-301a — Backend exposes PATCH (not POST) for archive.
      // The verb mismatch silently 405'd and surfaced as "Failed to
      // archive" for every admin trying to delete a shift.
      await api.patch(`/admin/shifts/${shiftId}/archive`);
      toast.success('Shift moved to Trash');
      navigate('/admin/shifts');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive');
      setDeleting(false);
    }
  };

  // ── Iter-133: 1-click decision shortcuts ──────────────────────────
  // Each calls the corresponding admin_decision_routes endpoint and
  // reloads the shift so the new booking row + applicant count refresh.
  const [bookingBestMatch, setBookingBestMatch] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);

  const handleBookBestMatch = async () => {
    if (!window.confirm('Book the top-ranked applicant as primary on this shift?')) return;
    setBookingBestMatch(true);
    try {
      const res = await api.post(`/admin/shifts/${shiftId}/book-best-match`);
      toast.success(`${res.data.talent_name} booked — match score ${Math.round(res.data.match_score)}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Book Best Match failed');
    } finally {
      setBookingBestMatch(false);
    }
  };

  const handleAutoFill = async () => {
    if (!window.confirm(
      'Auto-fill every open primary slot with the top-ranked applicants?\n\n' +
      'Applicants are ranked by match score (reliability, badges, proximity, etc.)'
    )) return;
    setAutoFilling(true);
    try {
      const res = await api.post(`/admin/shifts/${shiftId}/auto-fill`);
      const { filled = [], still_open = 0, applicants_scanned = 0 } = res.data || {};
      if (filled.length > 0) {
        toast.success(
          `Auto-fill: ${filled.length} booked, ${still_open} slot${still_open === 1 ? '' : 's'} still open ` +
          `(${applicants_scanned} applicants scanned)`
        );
      } else {
        toast.warning(`Auto-fill: no slots filled (${applicants_scanned} applicants scanned)`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-Fill failed');
    } finally {
      setAutoFilling(false);
    }
  };

  const handleHardDelete = async () => {
    setHardDeleting(true);
    try {
      const res = await api.delete(`/admin/shifts/${shiftId}`);
      if (res.data?.fallback === 'archived') {
        toast.warning(res.data.message || 'Hard delete blocked — shift was archived instead.');
      } else {
        toast.success('Gig permanently deleted');
      }
      navigate('/admin/shifts');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete shift');
      setHardDeleting(false);
    }
  };

  // 2026-06 — Impact preview so the admin sees what a permanent delete affects
  useEffect(() => {
    if (!shiftId) return;
    let cancelled = false;
    api.get(`/admin/shifts/${shiftId}/delete-impact`)
      .then((r) => { if (!cancelled) setDeleteImpact(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [shiftId]);

  // Auto-open invite modal if ?invite=1 is in URL (after creating a new shift)
  // Auto-open applicant drawer if ?drawer=applicants (from shift list cards).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (url.searchParams.get('invite') === '1') {
      setInviteOpen(true);
    }
    if (url.searchParams.get('drawer') === 'applicants') {
      setApplicantDrawerOpen(true);
    }
  }, []);

  const handleClone = async () => {
    setCloning(true);
    try {
      const res = await api.post(`/admin/shifts/${shiftId}/clone`);
      toast.success('Cloned — complete the new shift');
      navigate('/admin/shifts/create', { state: { prefill: res.data } });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clone failed');
    } finally {
      setCloning(false);
    }
  };

  // ── AI Requirement Organizer ──────────────────────────────────────
  const [aiOrg, setAiOrg] = useState(null); // {loading, sections, status}
  const AI_LABELS = {
    duties: 'Job duties & responsibilities', event_briefing: 'Event instructions & client briefing',
    performance_expectations: 'Performance expectations', reporting_requirements: 'Reporting requirements',
    required_photos: 'Required photos & documentation', dress_code: 'Dress code & grooming',
    parking_logistics: 'Parking & logistics', task_checklist: 'Task checklist',
  };
  const runOrganizeAI = async () => {
    setAiOrg({ loading: true, sections: null, status: 'proposed' });
    try {
      const res = await api.post(`/admin/shifts/${shiftId}/organize-requirements`);
      setAiOrg({ loading: false, sections: res.data.sections, status: 'proposed' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI could not organize this job');
      setAiOrg(null);
    }
  };
  const setAiSection = (k, v) => setAiOrg((o) => ({ ...o, sections: { ...o.sections, [k]: v } }));
  const approveAI = async () => {
    try {
      await api.post(`/admin/shifts/${shiftId}/requirement-draft/approve`, { sections: aiOrg.sections });
      toast.success('Approved — talent will now see the organized requirements');
      setAiOrg(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };
  const discardAI = async () => {
    try { await api.post(`/admin/shifts/${shiftId}/requirement-draft/discard`); } catch (_) {}
    setAiOrg(null);
  };


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/calendar/bookings/${shiftId}`);
      setData(res.data);
      setEdits({
        venue_name: res.data.shift.venue_name || '',
        venue_address: res.data.shift.venue_address || '',
        city: res.data.shift.city || '',
        state: res.data.shift.state || '',
        shift_date: res.data.shift.shift_date || '',
        start_time: (res.data.shift.start_time || '').slice(0, 5),
        end_time: (res.data.shift.end_time || '').slice(0, 5),
        talent_needed: res.data.shift.talent_needed || 1,
        bill_rate: res.data.shift.bill_rate || 0,
        talent_pay_rate: res.data.shift.talent_pay_rate ?? '',
        talent_pay_rate_min: res.data.shift.talent_pay_rate_min ?? '',
        talent_pay_rate_max: res.data.shift.talent_pay_rate_max ?? '',
        status: res.data.shift.status || 'open',
        duties: res.data.shift.duties || '',
        additional_notes: res.data.shift.additional_notes || '',
        important_notes: res.data.shift.important_notes || '',
        dress_code: res.data.shift.dress_code || '',
        confirmation_required_hours: res.data.shift.confirmation_required_hours ?? 24,
        cancellation_cutoff_hours: res.data.shift.cancellation_cutoff_hours ?? 24,
        backups_needed: res.data.shift.backups_needed ?? 0,
        backup_pay_type: res.data.shift.backup_pay_type || 'flat',
        backup_flat_pay: res.data.shift.backup_flat_pay ?? '',
        backup_hourly_rate: res.data.shift.backup_hourly_rate ?? '',
        // PO5 (revised) — backup workflow rules
        backup_mode: res.data.shift.backup_mode || 'on_site',
        backup_availability_start: res.data.shift.backup_availability_start || '',
        backup_availability_end: res.data.shift.backup_availability_end || '',
        backup_pay_requires_onsite: !!res.data.shift.backup_pay_requires_onsite,
        backup_auto_promote: res.data.shift.backup_auto_promote !== false,
        auto_close_enabled: res.data.shift.auto_close_enabled !== false,
        // Iter-182 — public-facing event title (editable on existing gigs)
        event_title: res.data.shift.event_title || '',
        // Iter-224f — cost code (multi-day invoice consolidation grouping)
        cost_code: res.data.shift.cost_code || '',
        // Iter-234h — Dress code preset + grooming brief + send-home warning.
        // Were never loaded into edit state, so the Edit Gig flow couldn't
        // touch them. Now editable end to end.
        dress_code_name: res.data.shift.dress_code_name || '',
        dress_code_description: res.data.shift.dress_code_description || '',
        dress_code_images: Array.isArray(res.data.shift.dress_code_images) ? res.data.shift.dress_code_images : [],
        dress_code_checklist: Array.isArray(res.data.shift.dress_code_checklist) ? res.data.shift.dress_code_checklist : [],
        attire_restrictions: Array.isArray(res.data.shift.attire_restrictions) ? res.data.shift.attire_restrictions : [],
        grooming_requirements: Array.isArray(res.data.shift.grooming_requirements) ? res.data.shift.grooming_requirements : [],
        grooming_images: Array.isArray(res.data.shift.grooming_images) ? res.data.shift.grooming_images : [],
        send_home_warning: !!res.data.shift.send_home_warning,
      });
    } catch {
      toast.error('Failed to load shift');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  // Auto-suggest a replacement on drop-out: when a staffing-gap alert deep-links
  // here with `?replace=1` and coverage is genuinely short with NO backup ready
  // to activate, open "Find Replacement Nearby" automatically (once).
  const [autoReplaceDone, setAutoReplaceDone] = useState(false);
  useEffect(() => {
    if (autoReplaceDone || loading || !data || searchParams.get('replace') !== '1') return;
    const sh = data.shift;
    const roster = data.assigned_active
      || (data.assigned_talent || []).filter(t => !['cancelled', 'no_show'].includes(t.booking_status));
    const primaries = roster.filter(t => !t.assignment_type || t.assignment_type === 'primary');
    const backups = roster.filter(t => t.assignment_type === 'backup');
    const atRisk = primaries.length < (sh?.talent_needed || 0);
    if (atRisk && backups.length === 0) {
      setInviteNearbyRadius(25);
      setInviteOpen(true);
    }
    setAutoReplaceDone(true);
  }, [loading, data, searchParams, autoReplaceDone]);

  // Load effective recap photo requirements for the editor.
  useEffect(() => {
    if (!shiftId) return;
    api.get(`/admin/shifts/${shiftId}/recap-requirements`).then((r) => {
      setRecapReq(r.data);
      setRecapMin(r.data.shift_photo_min != null ? String(r.data.shift_photo_min) : '');
      setRecapShots((r.data.shift_required_shots || []).join(', '));
    }).catch(() => {});
  }, [shiftId]);

  const saveRecapReq = async () => {
    setRecapSaving(true);
    try {
      const shots = recapShots.split(',').map((s) => s.trim()).filter(Boolean);
      await api.put(`/admin/shifts/${shiftId}/recap-requirements`, {
        photo_min: recapMin === '' ? null : parseInt(recapMin, 10),
        required_shots: shots.length ? shots : null,
      });
      toast.success('Photo requirements saved');
    } catch {
      toast.error('Failed to save requirements');
    } finally {
      setRecapSaving(false);
    }
  };

  const reopenMedia = async () => {
    try {
      const r = await api.post(`/admin/shifts/${shiftId}/reopen-media`);
      toast.success('Reopened for media uploads (auto-closes in 48h)');
      // Reflect the new window immediately (note is driven by recapReq).
      setRecapReq((prev) => ({ ...(prev || {}), media_reopened_until: r?.data?.media_reopened_until }));
      load();
    } catch {
      toast.error('Failed to reopen for media');
    }
  };

  // Iter-234h — auto-enter edit mode when navigated with `?edit=1`
  // (e.g. from the new dashboard EventDetailDrawer "Edit Gig" CTA).
  // Also load the curated dress code / grooming presets so the Edit
  // panel can render the same picker as the Create form.
  useEffect(() => {
    if (searchParams.get('edit') === '1' && data && !editMode) {
      setEditMode(true);
    }
  }, [searchParams, data, editMode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/presets/all');
        if (cancelled) return;
        setCuratedDressCodes(res.data?.dress_codes || []);
        setCuratedAttireRestrictions(res.data?.attire_restrictions || []);
        setCuratedGroomingReqs(res.data?.grooming_requirements || []);
      } catch {
        /* presets are optional — Edit panel falls back to free-text */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Iter-193 — bind the live shift to the InviteCart context so any
  // global launcher (Floating Bar, Invite Cart drawer) preselects THIS
  // shift instead of "first row from /open-next-14-days" (which used
  // to surface fake Miami Admin-Asst seeds).
  useEffect(() => {
    const s = data?.shift;
    if (!s?.id) return undefined;
    const spotsRemaining = Math.max(
      0,
      (Number(s.talent_needed) || 0) -
        ((data?.assigned_active || data?.assigned_talent || []).filter(
          (t) => ['confirmed', 'checked_in', 'completed'].includes(t.booking_status)
            && (!t.assignment_type || t.assignment_type === 'primary')
        ).length),
    );
    bindContextShift({
      id: String(s.id),
      event_title: s.event_title || `${s.role_name || 'Gig'}`,
      role_name: s.role_name || null,
      venue_name: s.venue_name || null,
      shift_date: s.shift_date || null,
      start_time: s.start_time || null,
      end_time: s.end_time || null,
      city: s.city || null,
      state: s.state || null,
      market_id: s.market_id || null,
      market_metro_name: s.market_metro_name || null,
      market_slug: s.market_slug || null,
      talent_needed: Number(s.talent_needed) || 0,
      spots_remaining: spotsRemaining,
    });
    return () => unbindContextShift();
  }, [data?.shift?.id, bindContextShift, unbindContextShift]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced talent search
  useEffect(() => {
    if (!talentQuery.trim()) { setTalentResults([]); return; }
    const t = setTimeout(async () => {
      setTalentSearching(true);
      try {
        const res = await api.get('/admin/users', {
          params: { role: 'talent', search: talentQuery, per_page: 20 },
        });
        setTalentResults(res.data.users || []);
      } catch {
        setTalentResults([]);
      } finally {
        setTalentSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [talentQuery]);

  // Per-talent rate input keyed by talentId for the search-results panel
  const [assignRates, setAssignRates] = useState({});

  const assign = async (talentId, assignmentType = 'primary') => {
    setAssigning(`${talentId}:${assignmentType}`);
    try {
      const params = { talent_id: talentId, assignment_type: assignmentType };
      const rateStr = assignRates[talentId];
      // Only PRIMARY assignments are validated against the shift's
      // primary pay range. Backups/alternates/extras are paid from the
      // backup pay fields, so applying the primary min/max range to them
      // produced a misleading "range restriction" when adding backups.
      if (rateStr !== undefined && rateStr !== '' && !Number.isNaN(parseFloat(rateStr))) {
        const rate = parseFloat(rateStr);
        const min = data?.shift?.talent_pay_rate_min;
        const max = data?.shift?.talent_pay_rate_max;
        params.talent_pay_rate = rate;
        if (assignmentType === 'primary'
            && ((min != null && rate < parseFloat(min)) || (max != null && rate > parseFloat(max)))) {
          // eslint-disable-next-line no-alert
          const reason = window.prompt(
            `$${rate} is outside the shift range $${min ?? '?'}–$${max ?? '?'}. Provide a reason to override:`
          );
          if (!reason) {
            setAssigning(null);
            return;
          }
          params.pay_rate_reason = reason;
        }
      }
      const res = await api.post(
        `/admin/shifts/${shiftId}/assign`,
        null,
        { params },
      );
      const label = res.data?.assignment_type || assignmentType;
      toast.success(
        label === 'primary'
          ? 'Talent assigned'
          : label === 'backup'
            ? 'Backup assigned'
            : 'Extra staff added',
      );
      setTalentQuery('');
      setTalentResults([]);
      setAssignRates({});
      await load();
    } catch (e) {
      // Iter-153 — GGF certification gate: offer admin override with reason
      // Iter-154 — universal profile-requirements gate: same UX pattern
      const detail = e.response?.data?.detail || '';
      if (/missing required profile information/i.test(detail)) {
        // eslint-disable-next-line no-alert
        const reason = window.prompt(
          `${detail}\n\nProvide a reason to override (will be logged to activity_logs):`,
        );
        if (!reason || !reason.trim()) {
          setAssigning(null);
          return;
        }
        try {
          const params2 = { talent_id: talentId, assignment_type: assignmentType,
                            override_profile_required: true, profile_override_reason: reason.trim() };
          const rateStr2 = assignRates[talentId];
          if (rateStr2 !== undefined && rateStr2 !== '' && !Number.isNaN(parseFloat(rateStr2))) {
            params2.talent_pay_rate = parseFloat(rateStr2);
          }
          await api.post(`/admin/shifts/${shiftId}/assign`, null, { params: params2 });
          toast.success('Profile override applied — talent assigned');
          setTalentQuery('');
          setTalentResults([]);
          setAssignRates({});
          await load();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Override failed');
        }
      } else if (/guardian garage floors/i.test(detail)) {
        // eslint-disable-next-line no-alert
        const reason = window.prompt(
          'This talent is not GGF-certified. Provide a reason to override the gate (will be logged to activity_logs):'
        );
        if (!reason || !reason.trim()) {
          setAssigning(null);
          return;
        }
        try {
          const params2 = { talent_id: talentId, assignment_type: assignmentType,
                            override_ggf_cert: true, ggf_override_reason: reason.trim() };
          const rateStr2 = assignRates[talentId];
          if (rateStr2 !== undefined && rateStr2 !== '' && !Number.isNaN(parseFloat(rateStr2))) {
            params2.talent_pay_rate = parseFloat(rateStr2);
          }
          await api.post(`/admin/shifts/${shiftId}/assign`, null, { params: params2 });
          toast.success('GGF override applied — talent assigned');
          setTalentQuery('');
          setTalentResults([]);
          setAssignRates({});
          await load();
        } catch (e2) {
          toast.error(e2.response?.data?.detail || 'Override failed');
        }
      } else {
        toast.error(detail || 'Failed to assign');
      }
    } finally {
      setAssigning(null);
    }
  };

  // Guard: high-impact changes (date/time/venue/rate) when talent is already assigned
  const [editConfirmOpen, setEditConfirmOpen] = useState(false);
  // Remove-talent flow: { booking_id, name, reason, notes } | null
  const [removeTalent, setRemoveTalent] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [findRepOpen, setFindRepOpen] = useState(false);

  // Pay-rate edit dialog state
  const [rateEdit, setRateEdit] = useState(null);
  const [rateSaving, setRateSaving] = useState(false);

  // Assigned Role change dialog state (Ops Queue #2)
  const [changeRole, setChangeRole] = useState(null); // { booking_id, name, current_role }
  const [crRoleId, setCrRoleId] = useState('');
  const [crPreview, setCrPreview] = useState(null);
  const [crRate, setCrRate] = useState('');
  const [crReason, setCrReason] = useState('');
  const [crBusy, setCrBusy] = useState(false);

  const openChangeRole = (t) => {
    setCrRoleId(''); setCrPreview(null); setCrRate(''); setCrReason('');
    setChangeRole({ booking_id: t.booking_id, name: t.name || t.email, current_role: t.role_name });
  };
  const onCrRolePick = async (roleId) => {
    setCrRoleId(roleId || '');
    setCrPreview(null); setCrRate('');
    if (!roleId || !changeRole) return;
    try {
      const { data } = await api.get(`/admin/bookings/${changeRole.booking_id}/change-role-preview`, { params: { role_id: roleId } });
      setCrPreview(data);
      if (data.applicable_pay_rate != null) setCrRate(String(data.applicable_pay_rate));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load role preview');
    }
  };
  const confirmChangeRole = async () => {
    if (!changeRole || !crRoleId) return;
    setCrBusy(true);
    try {
      const body = { role_id: crRoleId };
      if (crReason && crReason.trim()) body.reason = crReason.trim();
      if (crRate !== '' && !Number.isNaN(parseFloat(crRate))) body.talent_pay_rate = parseFloat(crRate);
      const { data } = await api.post(`/admin/bookings/${changeRole.booking_id}/change-role`, body);
      toast.success(`Role changed to ${data.to_role}${data.created_shift ? ' — new role-shift created' : ''}. Talent notified.`);
      setChangeRole(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Change role failed');
    } finally {
      setCrBusy(false);
    }
  };

  const submitRateEdit = async () => {
    if (!rateEdit) return;
    const newRate = parseFloat(rateEdit.current_rate);
    if (Number.isNaN(newRate) || newRate <= 0) {
      toast.error('Enter a valid rate');
      return;
    }
    if (!rateEdit.reason || rateEdit.reason.trim().length < 3) {
      toast.error('Please provide a reason (3+ chars) for the audit log');
      return;
    }
    setRateSaving(true);
    try {
      await api.patch(`/admin/bookings/${rateEdit.booking_id}/pay-rate`, {
        new_rate: newRate,
        reason: rateEdit.reason.trim(),
        force_override: !!rateEdit.force_override,
      });
      toast.success('Pay rate updated');
      setRateEdit(null);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to update rate';
      // Auto-prompt user to re-submit with force_override when range or lock blocked it
      if (e.response?.status === 400 || e.response?.status === 409) {
        if (window.confirm(`${detail}\n\nRe-submit as a force override?`)) {
          setRateEdit((prev) => prev && { ...prev, force_override: true });
          // user will hit Save again; do NOT auto-submit to avoid surprises
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setRateSaving(false);
    }
  };

  const hasAssignedTalent = () => {
    const bookings = data?.bookings || data?.assigned || [];
    return bookings.some((b) =>
      ['confirmed', 'pending_confirmation', 'checked_in'].includes(b.status)
    );
  };

  const highImpactChanged = () => {
    if (!data) return false;
    const s = data.shift;
    const origStart = (s.start_time || '').slice(0, 5);
    const origEnd = (s.end_time || '').slice(0, 5);
    return (
      edits.shift_date !== s.shift_date ||
      edits.start_time !== origStart ||
      edits.end_time !== origEnd ||
      String(edits.bill_rate) !== String(s.bill_rate) ||
      edits.venue_address !== (s.venue_address || '') ||
      edits.city !== (s.city || '') ||
      edits.state !== (s.state || '')
    );
  };

  const onSaveClick = () => {
    if (hasAssignedTalent() && highImpactChanged()) {
      setEditConfirmOpen(true);
      return;
    }
    save();
  };

  // Reason → UI metadata. Keeps impact copy in one place so the dialog
  // always reflects the backend's actual penalty.
  const REMOVAL_REASONS = [
    { value: 'call_out', label: 'Call Out', hint: 'Talent notified in advance. Minor reliability impact (−3).' },
    { value: 'last_minute_cancellation', label: 'Last Minute Cancellation', hint: 'Within cutoff. Moderate reliability impact (−5).' },
    { value: 'no_show', label: 'No Show', hint: 'Never arrived. Severe reliability impact (−10).' },
    { value: 'no_call_no_show', label: 'No Call / No Show', hint: 'Never arrived AND never notified. Strictest penalty (−15).' },
    { value: 'other', label: 'Other', hint: 'No reliability penalty applied.' },
  ];

  const saveTimeOverride = async () => {
    if (!timeEdit?.start_time || !timeEdit?.end_time) {
      toast.error('Enter both a start and end time.');
      return;
    }
    setSavingTime(true);
    try {
      const res = await api.patch(`/admin/bookings/${timeEdit.booking_id}/time`, {
        start_time: timeEdit.start_time,
        end_time: timeEdit.end_time,
        note: timeEdit.note || null,
      });
      toast.success(`Updated to ${res.data.effective_start} – ${res.data.effective_end}. Talent notified.`);
      setTimeEdit(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the time.');
    } finally {
      setSavingTime(false);
    }
  };

  const confirmRemoveTalent = async () => {    if (!removeTalent) return;
    setRemoving(true);
    const openReplaceAfter = removeTalent.openReplaceAfter;
    try {
      const res = await api.post(
        `/admin/bookings/${removeTalent.booking_id}/remove-talent`,
        { reason: removeTalent.reason, notes: removeTalent.notes || null }
      );
      const msg = res.data?.message || 'Talent removed';
      const impact = res.data?.score_impact || 0;
      if (impact !== 0) {
        toast.success(`${msg} — new score: ${res.data.new_reliability_score}`);
      } else {
        toast.success(msg);
      }
      if (res.data?.shift_reopened) {
        toast.info('Shift re-opened — the slot is available to fill again.');
      }
      setRemoveTalent(null);
      await load();
      if (openReplaceAfter) {
        // Scroll to the Manual Assign search so admin can immediately
        // pick a replacement.
        setTimeout(() => {
          const el = document.querySelector('[data-testid="talent-search-input"]');
          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
        }, 200);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove talent');
    } finally {
      setRemoving(false);
    }
  };

  const changeAssignmentType = async (bookingId, newType) => {
    try {
      const res = await api.patch(
        `/admin/bookings/${bookingId}/assignment-type`,
        null,
        { params: { new_type: newType } },
      );
      toast.success(res.data?.message || `Moved to ${newType}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reclassify');
    }
  };

  // PO14 — assign / unassign an Event Lead. Multiple leads per shift are
  // ALLOWED per product spec (large events may need leads by shift,
  // department, location, or role). Server records actor + timestamp for
  // audit. Missing 4xx handling is intentional — surface backend error.
  const toggleEventLead = async (bookingId, currentlyLead, name) => {
    const label = name || 'this talent';
    try {
      const res = await api.patch(
        `/admin/bookings/${bookingId}/event-lead`,
        { is_event_lead: !currentlyLead },
      );
      toast.success(
        res.data?.message ||
          (!currentlyLead ? `${label} marked as Event Lead` : `${label} removed as Event Lead`)
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update Event Lead');
    }
  };

  const save = async () => {
    setEditConfirmOpen(false);
    setSaving(true);
    try {
      const payload = {
        ...edits,
        talent_needed: parseInt(edits.talent_needed, 10),
        bill_rate: parseFloat(edits.bill_rate),
        talent_pay_rate: (edits.talent_pay_rate === '' || edits.talent_pay_rate === null)
          ? null : parseFloat(edits.talent_pay_rate),
        talent_pay_rate_min: (edits.talent_pay_rate_min === '' || edits.talent_pay_rate_min === null)
          ? null : parseFloat(edits.talent_pay_rate_min),
        talent_pay_rate_max: (edits.talent_pay_rate_max === '' || edits.talent_pay_rate_max === null)
          ? null : parseFloat(edits.talent_pay_rate_max),
        confirmation_required_hours: parseInt(edits.confirmation_required_hours, 10),
        cancellation_cutoff_hours: parseInt(edits.cancellation_cutoff_hours, 10),
        backups_needed: parseInt(edits.backups_needed || 0, 10),
        backup_flat_pay: edits.backup_flat_pay === '' || edits.backup_flat_pay === null
          ? null : parseFloat(edits.backup_flat_pay),
        backup_hourly_rate: edits.backup_hourly_rate === '' || edits.backup_hourly_rate === null
          ? null : parseFloat(edits.backup_hourly_rate),
        // PO5 (revised) — backup workflow rules
        backup_mode: edits.backup_mode || 'on_site',
        backup_availability_start: edits.backup_availability_start || null,
        backup_availability_end: edits.backup_availability_end || null,
        backup_pay_requires_onsite: !!edits.backup_pay_requires_onsite,
        backup_auto_promote: edits.backup_auto_promote !== false,
        auto_close_enabled: !!edits.auto_close_enabled,
      };
      await api.patch(`/admin/shifts/${shiftId}`, payload);
      toast.success('Shift updated');
      setEditMode(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-3xl mx-auto bg-white border rounded-xl p-8 text-center">
          <p className="text-gray-600">Shift not found.</p>
          <Button onClick={() => navigate('/admin/shifts')} className="mt-4">Back to Shifts</Button>
        </div>
      </div>
    );
  }

  const shift = data.shift;
  const assigned = data.assigned_talent || [];
  // 2026-06 FIX — historical roster loss. The backend `assigned_active` only
  // includes currently-active statuses (confirmed/checked_in/completed), so once a
  // booking advanced to timesheet_approved / paid it fell out of BOTH the active
  // and archived lists and the roster showed empty on completed/processed events
  // (e.g. Duluth Home Show). Derive the active roster from the full list so everyone
  // who was assigned/worked is permanently retained; only cancelled/no-show archive.
  const assignedActive   = assigned.filter(t => !['cancelled', 'no_show'].includes(t.booking_status));
  const assignedArchived = data.assigned_archived || assigned.filter(t =>  ['cancelled', 'no_show'].includes(t.booking_status));

  // Iter-133w (P0) + Iter-345 — Primary / Backup / Alternate / Extra
  // visual separation. Always split the active roster into 4 visible
  // sections so admins, clients, and on-site staff never confuse a
  // Backup or Alternate with a Primary. Null assignment_type defaults
  // to 'primary' for safety.
  const activePrimary   = assignedActive.filter(t => !t.assignment_type || t.assignment_type === 'primary');
  const activeBackup    = assignedActive.filter(t => t.assignment_type === 'backup');
  const activeAlternate = assignedActive.filter(t => t.assignment_type === 'alternate');
  const activeExtra     = assignedActive.filter(t => t.assignment_type === 'extra');
  // Admin promotion flow (Iter-133w) — enable the Promote button on a
  // backup row whenever this shift has at least one primary that ended
  // as a no-show. Admin can still promote manually via the dropdown, but
  // this keeps the happy-path 1-click.
  const hasNoShowPrimary = (assignedArchived || []).some(
    t => t.booking_status === 'no_show' && (!t.assignment_type || t.assignment_type === 'primary')
  );
  // One-tap "Activate Backup" is available whenever primary coverage is at
  // risk — a primary dropped out (released/cancelled/no-show) OR the active
  // primary count is below what the shift needs.
  const droppedPrimaries = (assignedArchived || []).filter(
    t => ['no_show', 'cancelled'].includes(t.booking_status)
      && (!t.assignment_type || t.assignment_type === 'primary')
  );
  const coverageAtRisk = activePrimary.length < (shift?.talent_needed || 0);
  const canActivateBackup = hasNoShowPrimary || coverageAtRisk || droppedPrimaries.length > 0;
  // Best-effort link to the most recent dropped primary for the audit trail.
  const droppedPrimaryForActivation = (() => {
    const d = droppedPrimaries[0];
    if (!d) return null;
    let reason = 'cancelled';
    if (d.booking_status === 'no_show') reason = 'no_show';
    else if ((d.cancelled_by || '').toLowerCase() === 'talent') reason = 'released';
    return { booking_id: d.booking_id, name: d.name || d.email, reason };
  })();
  const applicantCount   = data.applicant_count ?? 0;

  // PO15 — Assigned Talent Panel Redesign (2026-08-05)
  // Every row is a self-contained card so an admin can never be confused
  // about which action belongs to which person. Layout:
  //
  //   [Photo] Name · Role · Location            [Primary/Backup/Extra]
  //           email · phone                     [Confirmed / Pending]
  //                                             [★ Lead]  [🏆 badges]
  //
  //   ┌ Attendance ─────────────┐ ┌ Compliance ────┐ ┌ Hours ─────────┐
  //   │ In: 2:03 PM ✓           │ │ IDV ✓ · Stripe ✓│ │ Sched 8h · Act │
  //   │ Out: —                  │ │ BG ⚠            │ │ 4.5h           │
  //   │ GPS ✓ · Selfie ✓        │ └────────────────┘ └────────────────┘
  //   └─────────────────────────┘
  //
  //   Row-scoped actions:
  //     [★ Lead] [→ Primary/Backup/Extra] [Location] [Pay]
  //     [Replace] [Replace w/ Top] [Remove] [Mark No-Show] [Full Activity]
  //
  // NEVER add anonymous action buttons OUTSIDE this row — every action a
  // user takes on a person must be on that person's own card.
  const renderTalentRow = (t, opts = {}) => {
    const { canPromote = false } = opts;

    const initials = (t.name || t.email || '?')
      .split(/\s+/).map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
    const clockIn = t.clock_in_time ? new Date(t.clock_in_time) : null;
    const clockOut = t.clock_out_time ? new Date(t.clock_out_time) : null;
    // Actual clock times MUST render in the EVENT'S timezone (never the
    // admin's browser / GPS tz). Scheduled times are event-local wall-clock
    // strings, so the two now agree.
    const _evtTz = shift?.timezone;
    const _evtState = shift?.state;
    const fmtTime = (d, iso) => iso ? formatClockTimeInEventTz(iso, _evtTz, _evtState) : '—';
    const actualHours = clockIn && clockOut
      ? Math.round(((clockOut - clockIn) / 36e5) * 100) / 100
      : (t.hours_worked != null ? t.hours_worked : null);

    // ── Colour-coded pill helpers used inside the status grid ──
    const pillOk    = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    const pillWarn  = 'bg-amber-50 text-amber-700 border-amber-200';
    const pillErr   = 'bg-rose-50 text-rose-700 border-rose-200';
    const pillMuted = 'bg-slate-50 text-slate-600 border-slate-200';

    const gpsPill = (() => {
      if (!clockIn) return null;
      if (t.gps_status === 'verified') return { cls: pillOk,    label: 'GPS ✓',  title: 'GPS verified at check-in' };
      if (t.gps_status === 'flagged')  return { cls: pillErr,   label: 'GPS ⚠',  title: `Outside geofence (${t.gps_distance_meters ? Math.round(t.gps_distance_meters)+'m away' : 'flagged'})` };
      return { cls: pillWarn, label: 'GPS ?', title: 'GPS not verified' };
    })();
    const selfiePill = (() => {
      if (!clockIn) return null;
      if (t.selfie_status === 'captured') return { cls: pillOk,   label: 'Selfie ✓', title: 'Selfie captured at check-in' };
      return { cls: pillWarn, label: 'Selfie ?', title: 'No selfie on file' };
    })();

    const complianceOk = t.compliance_state === 'green';
    return (
      <div key={t.booking_id} className="space-y-1">
        <div
          className={`rounded-lg border bg-white shadow-sm p-4 w-full max-w-full box-border ${t.is_event_lead ? 'border-l-4 border-l-amber-500 border-t border-r border-b border-amber-200' : 'border-gray-200'}`}
          data-testid="assigned-talent-row"
          data-booking-id={t.booking_id}
          data-talent-id={t.talent_id}
          data-assignment-type={t.assignment_type || 'primary'}
          data-is-event-lead={t.is_event_lead ? 'true' : 'false'}
        >
          {/* ─── Row 1: Photo + Identity + Badges ─── */}
          <div className="flex items-start gap-3">
            <button
              type="button"
              onClick={() => (t.talent_id || t.user_id) && openProfile(t.talent_id || t.user_id)}
              className="shrink-0 focus:outline-none focus:ring-2 focus:ring-cyan-400 rounded-full"
              title={`Open ${t.name}'s profile`}
              data-testid={`talent-photo-${t.booking_id}`}
            >
              {t.profile_photo ? (
                <img
                  src={t.profile_photo}
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white ring-1 ring-gray-200"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center text-cyan-800 font-bold text-lg ring-1 ring-gray-200">
                  {initials}
                </div>
              )}
            </button>

            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <button
                  type="button"
                  onClick={() => (t.talent_id || t.user_id) && openProfile(t.talent_id || t.user_id)}
                  className="font-semibold text-gray-900 text-base hover:text-cyan-700 hover:underline text-left truncate max-w-[220px]"
                  title={t.name}
                  data-testid={`roster-talent-name-${t.booking_id}`}
                >
                  {t.name || t.email}
                </button>
                <AssignmentBadge type={t.assignment_type || 'primary'} size="md" />
                {t.is_event_lead && (
                  <Badge
                    className="bg-amber-100 text-amber-800 border-amber-300 border font-semibold uppercase tracking-wide text-[10px] px-1.5 py-0.5 inline-flex items-center"
                    data-testid={`event-lead-badge-${t.booking_id}`}
                    title="Designated Event Lead — has attendance authority"
                  >
                    <Star className="w-3 h-3 mr-1" /> Lead
                  </Badge>
                )}
                {/* Coverage-Integrity: true confirmation status — never reads
                    "Confirmed" until the talent personally confirmed. */}
                {['checked_in', 'completed', 'timesheet_approved'].includes(t.booking_status)
                  ? <StatusChip status={t.booking_status} />
                  : <BookingStatusBadge booking={t} size="md" />}
                {/* 2026-09 — Code of Conduct / gig acceptance (Booked → Accepted → Confirmed) */}
                {t.acceptance_pending ? (
                  <Badge className="bg-amber-50 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0.5" data-testid={`coc-pending-badge-${t.booking_id}`} title="Talent has not personally accepted the gig + Code of Conduct yet">
                    Acceptance pending
                  </Badge>
                ) : t.talent_accepted ? (
                  <Badge className="bg-teal-50 text-teal-800 border border-teal-300 text-[10px] px-1.5 py-0.5" data-testid={`coc-accepted-badge-${t.booking_id}`} title={t.talent_accepted_at ? `Accepted ${new Date(t.talent_accepted_at).toLocaleString()}${t.coc_version_label ? ' · CoC ' + t.coc_version_label : ''}` : 'Gig accepted'}>
                    ✓ Accepted{t.coc_version_label ? ` · ${t.coc_version_label}` : ''}
                  </Badge>
                ) : null}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setFlagTarget({ talentId: t.talent_id, talentName: t.name || t.email, bookingId: t.booking_id, shiftId }); }}
                  className="inline-flex items-center gap-0.5 text-[10px] font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-300 rounded px-1.5 py-0.5"
                  title="Flag a Code of Conduct breach"
                  data-testid={`flag-coc-btn-${t.booking_id}`}
                >
                  <Flag className="w-2.5 h-2.5" /> Flag
                </button>
                <NotPayableBadge
                  payable_status={t.payable_status}
                  payable_blockers={t.payable_blockers}
                  dataTestId={`payable-badge-${t.booking_id}`}
                />
              </div>
              <div className="text-xs text-gray-600 mt-0.5 flex flex-wrap items-center gap-x-2">
                {t.role_name && (
                  <span className="font-medium text-gray-700" data-testid={`talent-role-${t.booking_id}`}>
                    {t.role_name}
                  </span>
                )}
                {(t.city || t.state) && (
                  <span className="text-gray-500 truncate">
                    <MapPin className="inline w-3 h-3 mr-0.5" />
                    {[t.city, t.state].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 truncate" title={`${t.email || ''}${t.phone ? ' · ' + t.phone : ''}`}>
                {t.email}{t.phone ? ` · ${t.phone}` : ''}
              </div>
              {t.badges && t.badges.length > 0 && (
                <div className="mt-1.5"><BadgeRow badges={t.badges} max={5} /></div>
              )}
            </div>
          </div>

          {/* ─── Row 2: Status Grid (Attendance | Compliance | Hours) ─── */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`status-grid-${t.booking_id}`}>
            {/* Attendance */}
            <div className="rounded-md border border-gray-200 bg-gray-50/50 p-2" data-testid={`attendance-cell-${t.booking_id}`}>
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center">
                <Clock className="w-3 h-3 mr-1" /> Attendance
              </div>
              <div className="text-xs text-gray-800 leading-relaxed">
                <div>
                  <span className="text-gray-500">Clock In:</span>{' '}
                  <span className={clockIn ? 'font-semibold text-gray-900' : 'text-gray-400'}>
                    {fmtTime(clockIn, t.clock_in_time)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Clock Out:</span>{' '}
                  <span className={clockOut ? 'font-semibold text-gray-900' : 'text-gray-400'}>
                    {fmtTime(clockOut, t.clock_out_time)}
                  </span>
                </div>
              </div>
              {(gpsPill || selfiePill) && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {gpsPill && (
                    <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${gpsPill.cls}`}
                          title={gpsPill.title}
                          data-testid={`gps-pill-${t.booking_id}`}>
                      <MapPinned className="w-2.5 h-2.5 mr-0.5" /> {gpsPill.label}
                    </span>
                  )}
                  {selfiePill && (
                    <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${selfiePill.cls}`}
                          title={selfiePill.title}
                          data-testid={`selfie-pill-${t.booking_id}`}>
                      <Camera className="w-2.5 h-2.5 mr-0.5" /> {selfiePill.label}
                    </span>
                  )}
                </div>
              )}
              {/* Admin correction: undo an accidental early clock-out or fix the recorded time */}
              <ClockCorrectionControls
                compact
                booking={{
                  booking_id: t.booking_id,
                  clock_in_time: t.clock_in_time,
                  clock_out_time: t.clock_out_time,
                  booking_status: t.booking_status,
                  hours_approved: t.hours_approved,
                  timezone: shift?.timezone,
                  state: shift?.state,
                  break_minutes: t.break_minutes,
                  talent_pay_rate: t.talent_pay_rate,
                  talent_name: t.name || t.email,
                  venue_name: shift?.venue_name,
                  shift_date: shift?.shift_date,
                }}
                onDone={load}
              />
            </div>

            {/* Compliance */}
            <div className="rounded-md border border-gray-200 bg-gray-50/50 p-2" data-testid={`compliance-cell-${t.booking_id}`}>
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center">
                <Shield className="w-3 h-3 mr-1" /> Compliance
              </div>
              <div className="flex flex-wrap gap-1">
                <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${(t.idv_status === 'verified' || t.idv_status === 'approved') ? pillOk : pillWarn}`}
                      title={`Identity verification: ${t.idv_status || 'not started'}`}
                      data-testid={`idv-pill-${t.booking_id}`}>
                  IDV {(t.idv_status === 'verified' || t.idv_status === 'approved') ? '✓' : '⚠'}
                </span>
                <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${t.stripe_onboarded ? pillOk : pillErr}`}
                      title={t.stripe_onboarded ? 'Stripe Connect ready' : 'Stripe not onboarded — payouts blocked'}
                      data-testid={`stripe-pill-${t.booking_id}`}>
                  <DollarSign className="w-2.5 h-2.5 mr-0.5" /> Stripe {t.stripe_onboarded ? '✓' : '✗'}
                </span>
                {t.compliance_gaps && t.compliance_gaps.includes('background_check') && (
                  <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${pillWarn}`}
                        title="Background check gap"
                        data-testid={`bg-pill-${t.booking_id}`}>
                    BG ⚠
                  </span>
                )}
                {t.ggf_cert_override && (
                  <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${pillWarn}`}
                        title={t.ggf_cert_override_reason || 'GGF override applied'}
                        data-testid={`ggf-override-pill-${t.booking_id}`}>
                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> GGF Ovr
                  </span>
                )}
                {t.profile_override_applied && (
                  <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${pillWarn}`}
                        title={t.profile_override_reason || 'Profile override applied'}
                        data-testid={`profile-override-pill-${t.booking_id}`}>
                    <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Profile Ovr
                  </span>
                )}
                {complianceOk && !t.ggf_cert_override && !t.profile_override_applied && (
                  <span className={`inline-flex items-center text-[10px] font-medium border px-1.5 py-0.5 rounded ${pillOk}`}
                        title="All requirements met"
                        data-testid={`compliance-green-${t.booking_id}`}>
                    <CheckCircle className="w-2.5 h-2.5 mr-0.5" /> Green
                  </span>
                )}
              </div>
            </div>

            {/* Hours & Pay */}
            <div className="rounded-md border border-gray-200 bg-gray-50/50 p-2" data-testid={`hours-cell-${t.booking_id}`}>
              <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1 flex items-center">
                <Timer className="w-3 h-3 mr-1" /> Hours & Pay
              </div>
              <div className="text-xs text-gray-800 leading-relaxed">
                <div>
                  <span className="text-gray-500">Scheduled:</span>{' '}
                  <span className="font-semibold text-gray-900">
                    {t.scheduled_hours != null ? `${t.scheduled_hours}h` : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Actual:</span>{' '}
                  <span className={`font-semibold ${actualHours != null ? 'text-gray-900' : 'text-gray-400'}`}
                        data-testid={`actual-hours-${t.booking_id}`}>
                    {actualHours != null ? `${actualHours}h` : '—'}
                  </span>
                  {t.hours_approved && (
                    <span className="ml-1 text-[10px] text-emerald-700 font-medium">✓</span>
                  )}
                </div>
                {(t.effective_pay_rate != null || t.bill_rate != null) && (
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5" data-testid={`pay-row-${t.booking_id}`}>
                    {t.effective_pay_rate != null && (
                      <span className={`${t.pay_rate_outside_range ? 'text-amber-700' : 'text-gray-700'} text-[11px]`}>
                        ${t.effective_pay_rate.toFixed(2)}/hr
                        {t.is_rate_locked && <span className="ml-0.5 text-[9px] text-gray-400">(lock)</span>}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setRateEdit({
                        booking_id: t.booking_id, name: t.name,
                        current_rate: t.effective_pay_rate ?? '',
                        bill_rate: t.bill_rate,
                        min: data?.shift?.talent_pay_rate_min,
                        max: data?.shift?.talent_pay_rate_max,
                        locked: t.is_rate_locked,
                      })}
                      className="text-[10px] text-cyan-700 underline hover:text-cyan-900"
                      data-testid={`edit-rate-btn-${t.booking_id}`}
                    >
                      edit
                    </button>
                  </div>
                )}
                {t.margin != null && (
                  <div className={`text-[11px] ${t.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    Margin ${t.margin.toFixed(2)}{t.margin_percent != null && ` (${t.margin_percent}%)`}
                  </div>
                )}
                {t.expense_summary && (t.expense_summary.approved_total > 0 || t.expense_summary.pending_count > 0) && (
                  <div className="text-[11px] text-cyan-700 mt-0.5" data-testid={`expense-summary-${t.booking_id}`}>
                    {t.expense_summary.approved_total > 0 && (<>+${t.expense_summary.approved_total.toFixed(2)} reimb</>)}
                    {t.expense_summary.pending_count > 0 && (
                      <span className="ml-1 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-1 py-0.5 rounded">
                        {t.expense_summary.pending_count} pending
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── Row 3: Per-person actions (always scoped to THIS talent) ─── */}
          <div className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-gray-100" data-testid={`row-actions-${t.booking_id}`}>
            <Button size="sm" variant="outline"
              onClick={() => toggleEventLead(t.booking_id, t.is_event_lead, t.name)}
              className={t.is_event_lead
                ? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 shrink-0 font-semibold'
                : 'border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0'}
              data-testid={`toggle-lead-btn-${t.booking_id}`}
              title={t.is_event_lead ? 'Remove Event Lead designation' : 'Designate as Event Lead'}>
              <Star className="w-3.5 h-3.5 mr-1" /> {t.is_event_lead ? 'Remove Lead' : 'Assign Lead'}
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setLocBookingId(t.booking_id)}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 shrink-0"
              data-testid={`view-location-btn-${t.booking_id}`}
              title="View GPS clock-in location">
              <MapPinned className="w-3.5 h-3.5 mr-1" /> Location
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => payTalent(t)}
              disabled={payingBookingId === t.booking_id}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 shrink-0"
              data-testid={`pay-talent-btn-${t.booking_id}`}
              title="Trigger Stripe Connect payout">
              {payingBookingId === t.booking_id
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <><Banknote className="w-3.5 h-3.5 mr-1" /> Pay</>}
            </Button>
            {canPromote && t.assignment_type === 'backup' && (
              <Button size="sm" variant="outline"
                onClick={() => setPromoteTarget({ booking_id: t.booking_id, name: t.name || t.email })}
                className="border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 shrink-0 font-semibold"
                data-testid={`promote-to-primary-btn-${t.booking_id}`}
                title="Activate this backup as Primary to cover a drop-out (talent + client notified, audited)">
                ★ Activate Backup → Primary
              </Button>
            )}
            {t.assignment_type !== 'primary' && !canPromote && (
              <Button size="sm" variant="outline"
                onClick={() => changeAssignmentType(t.booking_id, 'primary')}
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 shrink-0"
                data-testid={`move-to-primary-btn-${t.booking_id}`}
                title="Move to primary slot">
                → Primary
              </Button>
            )}
            {t.assignment_type !== 'backup' && (
              <Button size="sm" variant="outline"
                onClick={() => changeAssignmentType(t.booking_id, 'backup')}
                className="border-amber-300 text-amber-700 hover:bg-amber-50 shrink-0"
                data-testid={`move-to-backup-btn-${t.booking_id}`}
                title="Move to backup slot (does not count toward talent needed)">
                → Backup
              </Button>
            )}
            {t.assignment_type !== 'extra' && (
              <Button size="sm" variant="outline"
                onClick={() => changeAssignmentType(t.booking_id, 'extra')}
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
                data-testid={`move-to-extra-btn-${t.booking_id}`}
                title="Move to extra/floater">
                → Extra
              </Button>
            )}
            <Button size="sm" variant="outline"
              onClick={() => setTimeEdit({ booking_id: t.booking_id, name: t.name || t.email, start_time: t.override_start_time || '', end_time: t.override_end_time || '', note: t.time_override_note || '' })}
              className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 shrink-0"
              data-testid={`edit-time-btn-${t.booking_id}`}
              title="Set an individual start/end time for just this talent (does not change the master shift)">
              <Clock className="w-3.5 h-3.5 mr-1" /> Edit Time
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => openChangeRole(t)}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
              data-testid={`change-role-btn-${t.booking_id}`}
              title="Move this talent to a different role on the same event (auto-creates the role-shift if needed)">
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Change Role
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setRemoveTalent({ booking_id: t.booking_id, name: t.name || t.email, reason: 'no_show', notes: 'Marked no-show by admin', openReplaceAfter: true })}
              className="border-red-400 bg-red-50 text-red-800 hover:bg-red-100 shrink-0"
              data-testid={`mark-no-show-btn-${t.booking_id}`}
              title="Mark as No Show — applies reliability penalty and triggers Smart Replacement">
              <UserX className="w-3.5 h-3.5 mr-1" /> Mark No Show
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setRemoveTalent({ booking_id: t.booking_id, name: t.name || t.email, reason: 'last_minute_cancellation', notes: '', openReplaceAfter: true })}
              className="border-orange-300 text-orange-700 hover:bg-orange-50 shrink-0"
              data-testid={`replace-talent-btn-${t.booking_id}`}
              title="Remove + open search to pick replacement">
              Replace
            </Button>
            <Button size="sm" variant="outline"
              onClick={async () => {
                if (!window.confirm(`Cancel ${t.name || t.email}'s booking and replace with the top-ranked pending applicant?`)) return;
                try {
                  const res = await api.post(`/admin/shifts/${shiftId}/replace-with-top`, { booking_id_to_replace: t.booking_id, reason: 'admin_replace_with_top' });
                  toast.success(`Replaced — ${res.data.talent_name} booked (match ${Math.round(res.data.match_score)})`);
                  await load();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Replace-with-top failed'); }
              }}
              disabled={applicantCount === 0}
              className="border-violet-400 text-violet-700 hover:bg-violet-50 shrink-0"
              data-testid={`replace-with-top-btn-${t.booking_id}`}
              title={applicantCount === 0 ? 'No applicants to rank' : 'Auto-swap with the highest-ranked pending applicant'}>
              <Zap className="w-3.5 h-3.5 mr-1" /> Replace w/ Top
            </Button>
            <Button size="sm" variant="outline"
              onClick={() => setRemoveTalent({ booking_id: t.booking_id, name: t.name || t.email, reason: 'last_minute_cancellation', notes: '' })}
              className="border-red-300 text-red-700 hover:bg-red-50 shrink-0"
              data-testid={`remove-talent-btn-${t.booking_id}`}
              title="Remove talent with cancellation reason">
              <UserMinus className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          </div>

          {/* ─── Row 4: Lifecycle timeline + audit deep-link ─── */}
          <div className="mt-3 pt-2 border-t border-gray-100">
            <LifecycleTimeline
              events={{
                invited_at: t.invited_at || t.booked_at,
                confirmed_at: t.confirmed_at || (t.confirmation_status === 'confirmed' ? t.booked_at : null),
                day_confirmed_at: t.day_confirmed_at,
                day_confirm_missed_at: t.day_confirm_missed_at,
                clock_in_time: t.clock_in_time,
                clock_out_time: t.clock_out_time,
                status: t.booking_status,
              }}
              dataTestId={`lifecycle-${t.booking_id}`}
            />
            <button
              type="button"
              onClick={() => navigate(`/admin/booking/${t.booking_id}/activity`)}
              className="mt-1 text-[11px] font-semibold text-cyan-700 hover:text-cyan-900 hover:underline"
              data-testid={`view-full-activity-${t.booking_id}`}
            >
              View Full Activity →
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-shift-detail-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
          {/* Row 1: Back button */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/shifts')} data-testid="back-button" className="-ml-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Shifts
            </Button>
            <div className="flex items-center gap-2">
              {/* Iter-224d — Operations Dashboard (tabbed) entry */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/operations/event/${shiftId}`)}
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                data-testid="admin-shift-operations-btn"
                title="Open the tabbed Operations Dashboard"
              >
                <FileText className="w-4 h-4 mr-1.5" /> Operations
              </Button>
              {/* Iter-224a — Close Out Event entry point */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/operations/closeout/${shiftId}`)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="admin-shift-closeout-btn"
                title="Open the reconcile & close-out wizard"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Close Out Event
              </Button>
              {editMode && (
                <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid="edit-mode-indicator">
                  Edit mode
                </span>
              )}
            </div>
          </div>

          {/* Iter-360c (P0-356 follow-up) — UNMISSABLE Unfilled Positions
              banner. Renders whenever open positions exist AND the shift
              is not cancelled. Provides an ALWAYS-VISIBLE Invite Talent
              CTA independent of the action bar below, so a layout
              regression on the primary action row can never orphan the
              Operations team from filling positions. Mobile-first: full
              width, no overflow. */}
          {!editMode && shift?.status !== 'cancelled' &&
            (Number(shift?.staffed_count ?? shift?.confirmed_count ?? 0) < Number(shift?.talent_needed ?? 0)) && (
            <div
              className="mb-3 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-cyan-300 bg-cyan-50 px-4 py-3"
              data-testid="unfilled-positions-banner"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-cyan-900" data-testid="unfilled-positions-headline">
                  {Math.max(0, Number(shift?.talent_needed ?? 0) - Number(shift?.staffed_count ?? shift?.confirmed_count ?? 0))}
                  {' '}open position{Math.max(0, Number(shift?.talent_needed ?? 0) - Number(shift?.staffed_count ?? shift?.confirmed_count ?? 0)) === 1 ? '' : 's'}
                  {' '}· {Number(shift?.staffed_count ?? shift?.confirmed_count ?? 0)} of {Number(shift?.talent_needed ?? 0)} staffed
                </p>
                <p className="text-xs text-cyan-800/80 mt-0.5">
                  Send invites to matching talent to fill the remaining {Number(shift?.talent_needed ?? 0) - Number(shift?.staffed_count ?? shift?.confirmed_count ?? 0) === 1 ? 'position' : 'positions'}.
                </p>
              </div>
              <Button
                onClick={() => setInviteOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white sm:shrink-0"
                data-testid="unfilled-positions-invite-btn"
              >
                <Send className="w-4 h-4 mr-2" /> Invite Talent
              </Button>
            </div>
          )}

          {/* Row 2: Action buttons —
              Iter-258: Mobile-friendly redesign.
              • 3 PRIMARY actions (Book Best Match, Auto-Fill, Mark Complete)
                stay inline so the fast paths remain one-tap.
              • Every secondary action is consolidated into a single
                "More Actions" dropdown to stop overflow + button-cut-off
                bugs on small screens.
              • Destructive actions (Delete / Hard Delete) live at the
                bottom of the menu separated by a divider. */}
          {!editMode ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-shift-action-bar">
              <Button
                variant="outline" size="sm"
                onClick={handleBookBestMatch}
                disabled={bookingBestMatch || applicantCount === 0 || (shift?.staffed_count ?? 0) >= (shift?.talent_needed ?? 0) || shift?.status === 'cancelled'}
                className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                data-testid="book-best-match-btn"
                title={
                  applicantCount === 0
                    ? 'No applicants to rank'
                    : ((shift?.staffed_count ?? 0) >= (shift?.talent_needed ?? 0))
                      ? 'Shift already fully staffed'
                      : 'Assign the top-ranked applicant as primary'
                }
              >
                {bookingBestMatch
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <Zap className="w-4 h-4 mr-1.5" />}
                Book Best Match
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={handleAutoFill}
                disabled={autoFilling || applicantCount === 0 || (shift?.staffed_count ?? 0) >= (shift?.talent_needed ?? 0) || shift?.status === 'cancelled'}
                className="border-violet-500 text-violet-700 hover:bg-violet-50"
                data-testid="auto-fill-btn"
                title={
                  applicantCount === 0
                    ? 'No applicants to rank'
                    : ((shift?.staffed_count ?? 0) >= (shift?.talent_needed ?? 0))
                      ? 'Shift already fully staffed'
                      : 'Fill every open slot with top-ranked applicants'
                }
              >
                {autoFilling
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <UserPlus className="w-4 h-4 mr-1.5" />}
                Auto-Fill
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={handleMarkComplete}
                disabled={markingComplete || shift?.status === 'completed' || shift?.status === 'cancelled'}
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="mark-shift-complete-btn"
                title={shift?.status === 'completed' ? 'Shift already completed' : 'Close primary bookings + auto-compute hours/pay'}
              >
                {markingComplete
                  ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  : <CheckCircle className="w-4 h-4 mr-1.5" />}
                Mark Shift Completed
              </Button>

              {/* Iter-314 — Hoisted Invite Talent OUT of the More Actions
                  dropdown. Admins were reporting the invite button was
                  "missing" on filled shifts (they didn't realise it was
                  hidden inside a dropdown). Making this a top-level
                  primary action removes the discoverability problem AND
                  supports re-inviting on already-filled shifts to reach
                  additional talent for last-minute changes. */}
              <Button
                variant="outline" size="sm"
                onClick={() => setInviteOpen(true)}
                disabled={shift?.status === 'cancelled'}
                className="border-cyan-500 text-cyan-700 hover:bg-cyan-50"
                data-testid="invite-talent-primary-btn"
                title="Send invitations to matching talent"
              >
                <Send className="w-4 h-4 mr-1.5" /> Invite Talent
              </Button>

              {/* More Actions consolidates the long-tail buttons. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="shift-more-actions-trigger"
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    <MoreHorizontal className="w-4 h-4 mr-1.5" /> More Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="shift-more-actions-menu">
                  <DropdownMenuLabel>Shift Actions</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => setInviteOpen(true)}
                    data-testid="more-action-invite-talent"
                  >
                    <Send className="w-4 h-4 mr-2 text-cyan-600" /> Invite Talent
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setRecipientsOpen(true)}
                    data-testid="more-action-sent-to"
                  >
                    <Inbox className="w-4 h-4 mr-2 text-indigo-600" /> Sent To
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/admin/shifts/${shiftId}/preview-as-talent`)}
                    data-testid="more-action-preview-talent"
                  >
                    <Smartphone className="w-4 h-4 mr-2 text-indigo-600" /> Preview as Talent
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={runOrganizeAI}
                    data-testid="more-action-organize-ai"
                  >
                    <Sparkles className="w-4 h-4 mr-2 text-fuchsia-600" /> Organize with AI
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleClone}
                    disabled={cloning}
                    data-testid="more-action-clone"
                  >
                    {cloning
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Copy className="w-4 h-4 mr-2 text-slate-600" />}
                    Clone Shift
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setEditMode(true)}
                    data-testid="more-action-edit-shift"
                  >
                    <Save className="w-4 h-4 mr-2 text-cyan-600" /> Edit Shift
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setReminderModalOpen(true)}
                    data-testid="more-action-send-reminder"
                  >
                    <Bell className="w-4 h-4 mr-2 text-amber-600" /> Send Reminder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setEmailModalOpen(true)}
                    data-testid="more-action-send-email"
                  >
                    <Mail className="w-4 h-4 mr-2 text-teal-600" /> Send Email
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={downloadSignInSheet}
                    data-testid="more-action-signin-sheet"
                  >
                    <FileText className="w-4 h-4 mr-2 text-slate-600" /> Sign-In Sheet PDF
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setDeleteOpen(true)}
                    className="text-red-700 focus:text-red-700 focus:bg-red-50"
                    data-testid="more-action-delete"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Shift
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => { setHardDeleteConfirm(''); setHardDeleteOpen(true); }}
                    className="text-red-800 focus:text-red-800 focus:bg-red-50"
                    data-testid="more-action-hard-delete"
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" /> Delete Gig Permanently
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="admin-shift-action-bar-edit">
              <Button variant="outline" onClick={() => { setEditMode(false); load(); }}>Cancel</Button>
              <Button onClick={onSaveClick} disabled={saving} className="bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="save-shift-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="outline"
                onClick={() => { setHardDeleteConfirm(''); setHardDeleteOpen(true); }}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="edit-delete-permanently-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete Permanently
              </Button>
            </div>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* PO-2026-06 #7 — Event Recap AI review */}
        <EventRecapAiCard shiftId={shiftId} />
        {/* P0.16 follow-up — surface platform balance right above the Pay buttons
            so admins never wonder "why did the button do nothing" (Anita Currie regression). */}
        <PlatformBalanceBanner compact data-testid="shift-balance-banner" />
        {/* Overview */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-xl">{shift.venue_name || '(no venue name)'}</CardTitle>
              <div className="text-sm text-gray-500 mt-1">
                {shift.company_name || '—'} · {shift.role_name}
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <UrgencyChips urgency={data.urgency} fillConfidence={data.fill_confidence} size="sm" />
                {shift.auto_close_enabled !== false ? (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border font-semibold uppercase tracking-wide text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200"
                    data-testid="auto-close-indicator-on"
                    title="Auto-close this shift when every primary booking has clocked out (and there are no hygiene blockers)"
                  >
                    <CheckCircle className="w-3 h-3" /> Auto-Close ON
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border font-semibold uppercase tracking-wide text-xs px-2 py-0.5 bg-gray-100 text-gray-600 border-gray-300"
                    data-testid="auto-close-indicator-off"
                    title="Admin will need to Mark Shift Completed manually"
                  >
                    Auto-Close OFF
                  </span>
                )}
              </div>
            </div>
            <Badge className={statusColors[shift.status]}>{shift.status}</Badge>
            {(shift.status === 'cancelled' || shift.status === 'filled') && (
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post(`/admin/shifts/${shiftId}/reopen-slot`);
                    toast.success('Slot reopened — qualified talent are being notified');
                    await load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to reopen slot');
                  }
                }}
                className="ml-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="reopen-slot-btn"
                title="Reopen this slot for new applications + Smart Replacement"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reopen Slot
              </Button>
            )}
            {shift.reopened_at && (
              <span className="ml-2 text-[11px] text-emerald-700 inline-flex items-center" data-testid="reopened-marker">
                <RefreshCw className="w-3 h-3 mr-0.5" /> Reopened {new Date(shift.reopened_at).toLocaleString()}
              </span>
            )}
          </CardHeader>
          <CardContent>
            {!editMode ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Date</div><div className="font-medium">{formatDateWithWeekday(shift.shift_date)}</div></div></div>
                <div className="flex items-start gap-2"><Clock className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Time</div><div className="font-medium" data-testid="shift-time-with-tz">{formatTimeRange(shift.start_time, shift.end_time)} <span className="text-xs text-gray-500">({timezoneLabel(shift.timezone)})</span></div></div></div>
                <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Location</div><div className="font-medium">{shift.city}, {shift.state}</div><div className="text-xs text-gray-500">{shift.venue_address}</div>
                  {(shift.venue_address || (shift.city && shift.state)) && (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(shift.venue_address || `${shift.venue_name || ''} ${shift.city} ${shift.state}`.trim())}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-cyan-600 hover:text-cyan-700"
                      data-testid="get-directions-btn"
                    >
                      <MapPinned className="w-3 h-3" /> Get Directions
                    </a>
                  )}
                </div></div>
                <div className="flex items-start gap-2"><Users className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Staffing</div><div className="font-medium" data-testid="shift-staffed-count">{shift.staffed_count ?? shift.confirmed_count ?? 0}/{shift.talent_needed} staffed</div></div></div>
                <div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Bill Rate</div><div className="font-medium">${shift.bill_rate?.toFixed(2)}/hr</div></div></div>
                <div className="flex items-start gap-2"><DollarSign className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Talent Pay</div><div className="font-medium" data-testid="shift-pay-rate-display">
                  ${shift.talent_pay_rate?.toFixed?.(2) ?? '—'}/hr
                  {shift.talent_pay_rate_min != null && shift.talent_pay_rate_max != null && (
                    <span className="ml-1 text-[11px] text-gray-500">range ${shift.talent_pay_rate_min}–${shift.talent_pay_rate_max}</span>
                  )}
                </div></div></div>
                <div className="flex items-start gap-2"><Users className="w-4 h-4 text-gray-400 mt-0.5" /><div><div className="text-gray-400 text-xs">Tier</div><div className="font-medium capitalize">{shift.tier_required}</div></div></div>
                {shift.duties && <div className="col-span-full"><div className="text-gray-400 text-xs">Duties</div><div className="text-sm">{shift.duties}</div></div>}
                {shift.dress_code && (
                  <div className="col-span-full p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dress-code-display">
                    <div className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Dress Code</div>
                    <div className="text-sm text-amber-900 mt-1 whitespace-pre-wrap">{shift.dress_code}</div>
                  </div>
                )}
                {(shift.dress_code_name || (shift.dress_code_images || []).length > 0 || (shift.dress_code_checklist || []).length > 0) && (
                  <div className="col-span-full p-3 bg-violet-50 border border-violet-200 rounded-lg" data-testid="dress-code-preset-display">
                    <div className="text-xs text-violet-700 font-semibold uppercase tracking-wide">Dress Code Preset {shift.dress_code_name ? `· ${shift.dress_code_name}` : ''}</div>
                    {shift.dress_code_description && (
                      <div className="text-sm text-violet-900 mt-1 whitespace-pre-wrap">{shift.dress_code_description}</div>
                    )}
                    {(shift.dress_code_images || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid="dress-code-images-display">
                        {(shift.dress_code_images || []).map((src, i) => (
                          <img key={i} src={src} alt={`dress ${i + 1}`} className="w-16 h-16 rounded border border-violet-200 object-cover" />
                        ))}
                      </div>
                    )}
                    {(shift.dress_code_checklist || []).length > 0 && (
                      <ul className="list-disc pl-5 mt-2 text-xs text-violet-900">
                        {(shift.dress_code_checklist || []).map((c, i) => <li key={i}>{c}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                {(shift.attire_restrictions || []).length > 0 && (
                  <div className="col-span-full p-3 bg-rose-50 border border-rose-200 rounded-lg" data-testid="attire-restrictions-display">
                    <div className="text-xs text-rose-700 font-semibold uppercase tracking-wide">Attire Restrictions</div>
                    <ul className="list-disc pl-5 mt-1 text-xs text-rose-900">
                      {(shift.attire_restrictions || []).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {(shift.grooming_requirements || []).length > 0 && (
                  <div className="col-span-full p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="grooming-requirements-display">
                    <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">Grooming Requirements</div>
                    <ul className="list-disc pl-5 mt-1 text-xs text-emerald-900">
                      {(shift.grooming_requirements || []).map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                    {(shift.grooming_images || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid="grooming-images-display">
                        {(shift.grooming_images || []).map((src, i) => (
                          <img key={i} src={src} alt={`grooming ${i + 1}`} className="w-16 h-16 rounded border border-emerald-200 object-cover" />
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {shift.send_home_warning && (
                  <div className="col-span-full p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-900" data-testid="send-home-warning-display">
                    <span className="font-semibold uppercase tracking-wide">Send Home Warning Enabled</span> — Talent who don't meet the dress code / grooming standards may be sent home without pay.
                  </div>
                )}
                <div className="col-span-full grid grid-cols-2 gap-3 text-xs">
                  <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                    <span className="text-gray-400">Confirm within:</span>{' '}
                    <span className="font-semibold text-gray-800">{shift.confirmation_required_hours ?? 24}h of booking</span>
                  </div>
                  <div className="p-2 bg-gray-50 border border-gray-200 rounded">
                    <span className="text-gray-400">Cancel cutoff:</span>{' '}
                    <span className="font-semibold text-gray-800">{shift.cancellation_cutoff_hours ?? 24}h before shift</span>
                  </div>
                </div>
                {shift.important_notes && (
                  <div className="col-span-full p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="important-notes-display">
                    <div className="text-xs text-blue-700 font-semibold uppercase tracking-wide">Important Notes / Instructions</div>
                    <div className="text-sm text-blue-900 mt-1">
                      <LinkifiedText text={shift.important_notes} />
                    </div>
                  </div>
                )}
                {shift.additional_notes && (
                  <div className="col-span-full">
                    <div className="text-gray-400 text-xs">Additional Notes</div>
                    <div className="text-sm"><LinkifiedText text={shift.additional_notes} /></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Iter-182 — Event Title (public-facing, editable on existing gigs) */}
                <div className="md:col-span-2">
                  <EventTitleField
                    value={edits.event_title}
                    onChange={(v) => setEdits({ ...edits, event_title: v })}
                    role_name={shift.role_name}
                    venue_name={edits.venue_name}
                    city={edits.city}
                    state={edits.state}
                    notes={edits.additional_notes}
                  />
                </div>
                {/* Iter-224f — Cost Code (consolidation grouping) */}
                <div className="md:col-span-2 space-y-2">
                  <Label className="flex items-center gap-2">
                    Cost Code
                    <span className="text-xs text-gray-500 font-normal">(optional · invoice grouping)</span>
                  </Label>
                  <Input
                    value={edits.cost_code || ''}
                    onChange={(e) => setEdits({ ...edits, cost_code: e.target.value })}
                    placeholder="e.g. MKT-2026-Q3 or EVT-DALLAS-HOME-SHOW"
                    maxLength={64}
                    data-testid="shift-detail-cost-code-input"
                  />
                  <p className="text-xs text-gray-500">
                    Multi-day shifts sharing the same client, event, venue, city,
                    and cost code roll up into one consolidated invoice on closeout.
                  </p>
                </div>
                <div className="space-y-2"><Label>Venue Name <span className="text-[10px] text-gray-400">(Google auto-fill)</span></Label>
                  <AddressAutocomplete
                    value={edits.venue_name}
                    onChange={(v) => setEdits({ ...edits, venue_name: v })}
                    onSelect={(p) => {
                      if (!p) return;
                      setEdits((e) => ({
                        ...e,
                        venue_name: p.name || e.venue_name,
                        venue_address: p.address || e.venue_address,
                        city: p.city || e.city,
                        state: p.state_code || p.state || e.state,
                      }));
                    }}
                    searchType="establishment"
                    placeholder="e.g. Nashville Fairgrounds"
                    country="us"
                    data-testid="edit-venue-name-autocomplete"
                  />
                </div>
                <div className="space-y-2"><Label>Status</Label>
                  <select className="w-full border rounded-md p-2 text-sm" value={edits.status} onChange={(e) => setEdits({ ...edits, status: e.target.value })} data-testid="edit-status">
                    <option value="open">open</option>
                    <option value="confirmed">confirmed</option>
                    <option value="in_progress">in_progress</option>
                    <option value="completed">completed</option>
                    <option value="cancelled">cancelled</option>
                  </select>
                </div>
                <div className="space-y-2 md:col-span-2"><Label>Venue Address</Label>
                  <AddressAutocomplete
                    value={edits.venue_address}
                    onChange={(v) => setEdits({ ...edits, venue_address: v })}
                    onSelect={(p) => {
                      if (!p) return;
                      setEdits((e) => ({
                        ...e,
                        venue_address: p.address || e.venue_address,
                        city: p.city || e.city,
                        state: p.state_code || p.state || e.state,
                      }));
                    }}
                    searchType="address"
                    placeholder="Street address"
                    country="us"
                    data-testid="edit-venue-address-autocomplete"
                  />
                </div>
                <div className="space-y-2"><Label>City</Label><Input value={edits.city} onChange={(e) => setEdits({ ...edits, city: e.target.value })} data-testid="edit-city" /></div>
                <div className="space-y-2"><Label>State</Label><Input value={edits.state} onChange={(e) => setEdits({ ...edits, state: e.target.value })} placeholder="e.g. GA" maxLength={2} data-testid="edit-state" /></div>
                <div className="space-y-2"><Label>Shift Date</Label><Input type="date" value={edits.shift_date} onChange={(e) => setEdits({ ...edits, shift_date: e.target.value })} data-testid="edit-shift-date" /></div>
                {/* Iter-411p — Multi-Day Event control restored to Edit form.
                    Backend endpoints (/api/admin/multi-slot-events/...) have
                    supported this since Iter-235; only the wiring in the Edit
                    page was missing. The button below opens EventScheduleEditor
                    which auto-promotes a solo shift to a multi-day series on
                    first "Add date" and preserves shared fields (client, event
                    title, venue, position) across every date. */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <div>
                      <div className="font-medium text-cyan-900 text-sm">Multi-Day Event / Additional Dates</div>
                      <p className="text-xs text-cyan-800 mt-0.5">
                        Add, remove, or edit event dates without duplicating the client, venue, event
                        name, position, or instructions. Existing bookings, attendance, and briefings
                        are preserved. Removing a date with booked talent uses the same cancellation
                        rules as the shift-level flow.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      className="border-cyan-400 bg-white hover:bg-cyan-100 text-cyan-800"
                      onClick={() => setEventScheduleOpen(true)}
                      data-testid="open-event-schedule-editor"
                    >
                      Manage dates
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 grid grid-cols-2 gap-2">
                  <div><Label>Start Time</Label><Input type="time" value={edits.start_time} onChange={(e) => setEdits({ ...edits, start_time: e.target.value })} data-testid="edit-start-time" /></div>
                  <div><Label>End Time</Label><Input type="time" value={edits.end_time} onChange={(e) => setEdits({ ...edits, end_time: e.target.value })} data-testid="edit-end-time" /></div>
                </div>
                <div className="space-y-2"><Label>Talent Needed</Label><Input type="number" min="1" value={edits.talent_needed} onChange={(e) => setEdits({ ...edits, talent_needed: e.target.value })} data-testid="edit-talent-needed" /></div>
                <div className="space-y-2"><Label>Bill Rate ($/hr)</Label><Input type="number" step="0.01" value={edits.bill_rate} onChange={(e) => setEdits({ ...edits, bill_rate: e.target.value })} data-testid="edit-bill-rate" /></div>
                <div className="space-y-2"><Label>Talent Pay Rate ($/hr) <span className="text-[11px] text-gray-400">(default)</span></Label><Input type="number" step="0.01" value={edits.talent_pay_rate} onChange={(e) => setEdits({ ...edits, talent_pay_rate: e.target.value })} placeholder="blank = auto-calc" data-testid="edit-talent-pay-rate" /></div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Pay Range <span className="text-[11px] text-gray-400">(optional — for per-talent rates by experience)</span></Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" value={edits.talent_pay_rate_min ?? ''} onChange={(e) => setEdits({ ...edits, talent_pay_rate_min: e.target.value })} placeholder="Min ($/hr)" data-testid="edit-talent-pay-rate-min" />
                    <Input type="number" step="0.01" value={edits.talent_pay_rate_max ?? ''} onChange={(e) => setEdits({ ...edits, talent_pay_rate_max: e.target.value })} placeholder="Max ($/hr)" data-testid="edit-talent-pay-rate-max" />
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2"><Label>Duties</Label><Textarea rows={3} value={edits.duties} onChange={(e) => setEdits({ ...edits, duties: e.target.value })} /></div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Dress Code (free-text fallback)</Label>
                  <Textarea
                    rows={2}
                    value={edits.dress_code}
                    onChange={(e) => setEdits({ ...edits, dress_code: e.target.value })}
                    placeholder="Used only if no Dress Code Preset is selected below."
                    data-testid="edit-dress-code"
                  />
                </div>

                {/* Iter-234h — Visual Dress Code + Grooming module.
                    Single source of truth shared with Create Gig so the
                    Create / Edit / Client / Talent experience stays in
                    sync (image cards, preset gallery, attire + grooming
                    checkboxes, Send Home Warning). */}
                <div className="md:col-span-2">
                  <DressCodeGroomingPicker
                    value={{
                      dress_code_name: edits.dress_code_name,
                      dress_code_description: edits.dress_code_description,
                      dress_code_images: edits.dress_code_images,
                      dress_code_checklist: edits.dress_code_checklist,
                      attire_restrictions: edits.attire_restrictions,
                      grooming_requirements: edits.grooming_requirements,
                      send_home_warning: edits.send_home_warning,
                    }}
                    onChange={(next) => setEdits((prev) => ({ ...prev, ...next }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm Within (hours)</Label>
                  <Input
                    type="number" min="1" max="168"
                    value={edits.confirmation_required_hours}
                    onChange={(e) => setEdits({ ...edits, confirmation_required_hours: e.target.value })}
                    data-testid="edit-confirm-hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cancel Cutoff (hours before shift)</Label>
                  <Input
                    type="number" min="0" max="168"
                    value={edits.cancellation_cutoff_hours}
                    onChange={(e) => setEdits({ ...edits, cancellation_cutoff_hours: e.target.value })}
                    data-testid="edit-cancel-hours"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Important Notes / Instructions</Label>
                  <Textarea
                    rows={4}
                    value={edits.important_notes}
                    onChange={(e) => setEdits({ ...edits, important_notes: e.target.value })}
                    placeholder="Arrival, parking, onsite contact, brand talking points..."
                    data-testid="edit-important-notes"
                  />
                </div>
                <div className="space-y-2 md:col-span-2"><Label>Additional Notes</Label><Textarea rows={2} value={edits.additional_notes} onChange={(e) => setEdits({ ...edits, additional_notes: e.target.value })} /></div>

                {/* Backup staffing */}
                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Backup Staffing</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Backups Needed</Label>
                      <Input
                        type="number" min="0" max="20"
                        value={edits.backups_needed}
                        onChange={(e) => setEdits({ ...edits, backups_needed: e.target.value })}
                        data-testid="edit-backups-needed"
                      />
                    </div>
                    {parseInt(edits.backups_needed || 0, 10) > 0 && (
                      <>
                        <div className="space-y-2">
                          <Label>Backup Pay Type</Label>
                          <select
                            className="w-full border rounded-md p-2 text-sm"
                            value={edits.backup_pay_type}
                            onChange={(e) => setEdits({ ...edits, backup_pay_type: e.target.value })}
                            data-testid="edit-backup-pay-type"
                          >
                            <option value="flat">Flat standby pay</option>
                            <option value="hourly">Hourly (if activated)</option>
                          </select>
                        </div>
                        {edits.backup_pay_type === 'flat' ? (
                          <div className="space-y-2 md:col-span-2">
                            <Label>Flat Standby Pay ($)</Label>
                            <Input
                              type="number" step="0.01" min="0"
                              value={edits.backup_flat_pay}
                              onChange={(e) => setEdits({ ...edits, backup_flat_pay: e.target.value })}
                              placeholder="e.g. 50.00"
                              data-testid="edit-backup-flat-pay"
                            />
                          </div>
                        ) : (
                          <div className="space-y-2 md:col-span-2">
                            <Label>Backup Hourly Rate ($/hr)</Label>
                            <Input
                              type="number" step="0.01" min="0"
                              value={edits.backup_hourly_rate}
                              onChange={(e) => setEdits({ ...edits, backup_hourly_rate: e.target.value })}
                              placeholder="e.g. 30.00"
                              data-testid="edit-backup-hourly-rate"
                            />
                          </div>
                        )}

                        {/* PO5 (revised) — Backup workflow rules */}
                        <div className="space-y-2 md:col-span-2 border-t pt-3 mt-1">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            Backup Workflow Rules
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-full px-1.5 py-0.5">PO5</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Backup Mode</Label>
                          <select
                            className="w-full border rounded-md p-2 text-sm"
                            value={edits.backup_mode}
                            onChange={(e) => setEdits({ ...edits, backup_mode: e.target.value })}
                            data-testid="edit-backup-mode"
                          >
                            <option value="on_site">On-site (default)</option>
                            <option value="on_call">On-call (off-site, reachable)</option>
                          </select>
                          <p className="text-[11px] text-gray-500">
                            {edits.backup_mode === 'on_call'
                              ? 'Backup stays off-site but reachable, responds within response window.'
                              : 'Backup reports to the venue and remains available during the availability window.'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label>Availability Window</Label>
                          <div className="flex items-center gap-1">
                            <Input
                              type="datetime-local"
                              value={edits.backup_availability_start ? String(edits.backup_availability_start).slice(0,16) : ''}
                              onChange={(e) => setEdits({ ...edits, backup_availability_start: e.target.value })}
                              placeholder="Start"
                              data-testid="edit-backup-avail-start"
                            />
                            <span className="text-gray-400">–</span>
                            <Input
                              type="datetime-local"
                              value={edits.backup_availability_end ? String(edits.backup_availability_end).slice(0,16) : ''}
                              onChange={(e) => setEdits({ ...edits, backup_availability_end: e.target.value })}
                              placeholder="End"
                              data-testid="edit-backup-avail-end"
                            />
                          </div>
                          <p className="text-[11px] text-gray-500">Defaults to the shift's own window when left empty.</p>
                        </div>
                        <div className="space-y-2 md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <label className="flex items-start gap-2 p-2 rounded-md border border-gray-200 bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!edits.backup_pay_requires_onsite}
                              onChange={(e) => setEdits({ ...edits, backup_pay_requires_onsite: e.target.checked })}
                              className="mt-1"
                              data-testid="edit-backup-pay-requires-onsite"
                            />
                            <div className="text-xs">
                              <div className="font-semibold text-gray-800">Backup pay requires full on-site window</div>
                              <div className="text-gray-500 mt-0.5">
                                When checked, the flat backup pay is only earned if the talent stays available for the entire backup window.
                              </div>
                            </div>
                          </label>
                          <label className="flex items-start gap-2 p-2 rounded-md border border-gray-200 bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={edits.backup_auto_promote !== false}
                              onChange={(e) => setEdits({ ...edits, backup_auto_promote: e.target.checked })}
                              className="mt-1"
                              data-testid="edit-backup-auto-promote"
                            />
                            <div className="text-xs">
                              <div className="font-semibold text-gray-800">Auto-offer promotion on primary drop</div>
                              <div className="text-gray-500 mt-0.5">
                                On a primary no-show/cancel, automatically send a promotion OFFER to a backup. Talent must confirm availability before it activates.
                              </div>
                            </div>
                          </label>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Iter-133: Auto-Close toggle */}
                <div className="md:col-span-2 border-t pt-4 mt-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Automation</div>
                  <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Label className="font-semibold text-gray-800">Auto-Close Shift</Label>
                        <span className="text-[10px] uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">Iter-133</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 max-w-md">
                        Automatically close this shift once every primary booking has clocked out.
                        Skipped if any booking has a clock-out hygiene anomaly.
                      </p>
                    </div>
                    <Switch
                      checked={!!edits.auto_close_enabled}
                      onCheckedChange={(v) => setEdits({ ...edits, auto_close_enabled: !!v })}
                      data-testid="edit-auto-close-toggle"
                    />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* iter417/iter418 — Onsite / Arrival Details (editable inline; Evie reads these) */}
        <OnsiteArrivalCard
          shift={shift}
          method="patch"
          endpoint={`/admin/shifts/${shiftId}`}
          canEdit={true}
          onSaved={load}
        />

        {/* Live Rate Calculator (admin-only view) */}
        <LiveRateCalculator
          billRate={editMode ? edits.bill_rate : shift.bill_rate}
          talentPayRate={editMode
            ? (edits.talent_pay_rate !== '' && edits.talent_pay_rate != null
                ? edits.talent_pay_rate
                : shift.talent_pay_rate)
            : shift.talent_pay_rate}
          hours={(() => {
            const st = (editMode ? null : shift.start_time) || shift.start_time || '0:0';
            const et = (editMode ? null : shift.end_time) || shift.end_time || '0:0';
            const [sh, sm] = String(st).split(':').map(Number);
            const [eh, em] = String(et).split(':').map(Number);
            let mins = (eh * 60 + em) - (sh * 60 + sm);
            if (mins < 0) mins += 24 * 60;
            return +(mins / 60).toFixed(2);
          })()}
          staffCount={editMode ? edits.talent_needed : shift.talent_needed}
        />

        {/* Payout Terms (talent-facing disclosure) */}
        <PayoutTermsCard shiftId={shiftId} />

        {/* Attachments */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-cyan-500" />
              Attachments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ShiftAttachments shiftId={shiftId} adminMode={true} />
          </CardContent>
        </Card>

        {/* Attendance & Compliance (selfies, GPS pins, overrides) */}
        <AttendanceCompliancePanel shiftId={shiftId} />
        <div className="mt-3">
          <ShiftEventCodeCard shiftId={shiftId} />
        </div>

        {/* Post-Event Submissions (talent photos + reports) */}
        <PostEventSubmissionsPanel shiftId={shiftId} />

        {/* Live interactive-task monitoring */}
        <div className="mt-6">
          <ShiftTaskMonitor shiftId={shiftId} pollMs={30000} />
        </div>

        {/* Coverage summary — real confirmations only */}
        <div className="mb-4">
          <CoveragePanel
            shiftId={shiftId}
            refreshKey={`${(data?.assigned_active || []).length}:${(data?.assigned_active || []).filter(t => t.is_confirmed || ['confirmed','day_confirmed'].includes(t.confirmation_status)).length}`}
          />
        </div>

        {/* Assigned Talent */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-lg" data-testid="assigned-talent-count">
                Assigned Talent ({(shift.staffed_count ?? shift.confirmed_count ?? 0)}/{shift.talent_needed}
                {shift.backups_needed > 0 && (shift.backup_staffed_count ?? shift.backup_confirmed_count ?? 0) > 0 && (
                  <span className="text-xs font-normal text-amber-700 ml-2">
                    +{shift.backup_staffed_count ?? shift.backup_confirmed_count ?? 0} backup
                  </span>
                )}
                {(shift.extra_staffed_count ?? shift.extra_confirmed_count ?? 0) > 0 && (
                  <span className="text-xs font-normal text-indigo-700 ml-2">
                    +{shift.extra_staffed_count ?? shift.extra_confirmed_count ?? 0} extra
                  </span>
                )})
              </CardTitle>
              {applicantCount > 0 && (
                <Button
                  variant="outline" size="sm"
                  onClick={() => setApplicantDrawerOpen(true)}
                  className="border-indigo-400 text-indigo-700 hover:bg-indigo-50"
                  data-testid="open-applicants-drawer-btn"
                  title="View applicants"
                >
                  <Users className="w-4 h-4 mr-1.5" />
                  {applicantCount} applicant{applicantCount === 1 ? '' : 's'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/70 p-3" data-testid="recap-requirements-editor">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <span className="text-xs font-semibold text-slate-700">Recap photo requirements</span>
                {recapReq && recapReq.shift_photo_min == null && (recapReq.client_photo_min != null || (recapReq.client_required_shots || []).length > 0) && (
                  <span className="text-[10px] text-slate-400">Inheriting client default (min {recapReq.client_photo_min || 0}{(recapReq.client_required_shots || []).length ? `, shots: ${recapReq.client_required_shots.join(', ')}` : ''})</span>
                )}
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div>
                  <label className="block text-[10px] text-slate-500 mb-0.5">Min photos</label>
                  <input type="number" min="0" value={recapMin} onChange={(e) => setRecapMin(e.target.value)}
                    placeholder="0" className="w-20 h-8 text-sm rounded-md border border-slate-200 px-2" data-testid="recap-min-input" />
                </div>
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-[10px] text-slate-500 mb-0.5">Required shots (comma-separated)</label>
                  <input type="text" value={recapShots} onChange={(e) => setRecapShots(e.target.value)}
                    placeholder="Setup, Crowd, Display" className="w-full h-8 text-sm rounded-md border border-slate-200 px-2" data-testid="recap-shots-input" />
                </div>
                <Button size="sm" variant="outline" onClick={saveRecapReq} disabled={recapSaving} data-testid="save-recap-req-btn">
                  {recapSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" className="text-cyan-700" onClick={reopenMedia} data-testid="reopen-media-btn"
                  title="Reopen this shift for late media uploads (auto-closes in 48h)">
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reopen for media
                </Button>
              </div>
              {recapReq?.media_reopened_until && (
                <p className="text-[10px] text-emerald-600 mt-1.5" data-testid="media-reopened-note">
                  Media uploads reopened until {new Date(recapReq.media_reopened_until).toLocaleString()}
                </p>
              )}
            </div>
            {coverageAtRisk && (
              <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="coverage-at-risk-banner">
                <div className="text-sm text-rose-800">
                  <span className="font-semibold">Coverage at risk</span> — {activePrimary.length}/{shift.talent_needed || 0} primary confirmed.
                  {droppedPrimaries.length > 0 && ' A primary dropped out.'}
                  {activeBackup.length > 0 && ' Activate a backup below, or invite a replacement.'}
                </div>
                <Button
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-700 text-white shrink-0"
                  onClick={() => { setInviteNearbyRadius(25); setInviteOpen(true); }}
                  data-testid="find-replacement-nearby-btn"
                  title="Invite the closest available, qualified, unbooked talent from the venue"
                >
                  <MapPin className="w-4 h-4 mr-1.5" /> Find Replacement Nearby
                </Button>
              </div>
            )}
            {assignedActive.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="no-active-talent-msg">No active talent on this shift.</p>
            ) : (
              <div className="space-y-4" data-testid="assigned-active-list">
                {activePrimary.length > 0 && (
                  <div data-testid="primary-talent-section">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-emerald-200">
                      <AssignmentBadge type="primary" size="md" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        ({activePrimary.length}/{shift.talent_needed || 0})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {activePrimary.map((t) => renderTalentRow(t))}
                    </div>
                  </div>
                )}
                {activeBackup.length > 0 && (
                  <div data-testid="backup-talent-section">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-amber-200">
                      <AssignmentBadge type="backup" size="md" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        ({activeBackup.length}/{shift.backups_needed || 0})
                      </span>
                      {hasNoShowPrimary && (
                        <span className="text-[10px] text-rose-700 font-medium">
                          Primary no-show detected — promote a backup below
                        </span>
                      )}
                      {!hasNoShowPrimary && coverageAtRisk && (
                        <span className="text-[10px] text-rose-700 font-medium" data-testid="coverage-at-risk-hint">
                          Coverage at risk — activate a backup below
                        </span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {activeBackup.map((t) => renderTalentRow(t, { canPromote: canActivateBackup }))}
                    </div>
                  </div>
                )}
                {activeAlternate.length > 0 && (
                  <div data-testid="alternate-talent-section">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                      <AssignmentBadge type="alternate" size="md" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        ({activeAlternate.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {activeAlternate.map((t) => renderTalentRow(t, { canPromote: true }))}
                    </div>
                  </div>
                )}
                {activeExtra.length > 0 && (
                  <div data-testid="extra-talent-section">
                    <div className="flex items-center gap-2 mb-2 pb-1 border-b border-slate-200">
                      <AssignmentBadge type="extra" size="md" />
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                        ({activeExtra.length})
                      </span>
                    </div>
                    <div className="space-y-2">
                      {activeExtra.map((t) => renderTalentRow(t))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Iter-132: Archived (cancelled / no-show / replaced) roster.
                Collapsed by default so the active list stays clean. */}
            {assignedArchived.length > 0 && (
              <div className="mt-4 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowArchived(!showArchived)}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1"
                  data-testid="toggle-archived-roster-btn"
                >
                  {showArchived ? '▼' : '▶'} Archived · {assignedArchived.length}
                  <span className="text-gray-400 font-normal ml-1">(cancelled / no-show / replaced)</span>
                </button>
                {showArchived && (
                  <ul className="mt-2 space-y-1" data-testid="assigned-archived-list">
                    {assignedArchived.map((t) => (
                      <li
                        key={t.booking_id}
                        className="text-xs text-gray-500 flex items-center gap-2 py-1 px-2 rounded bg-gray-50 border border-gray-200"
                        data-testid={`archived-row-${t.booking_id}`}
                      >
                        <span className="font-medium text-gray-700 truncate max-w-[40%]">
                          {t.name || t.email}
                        </span>
                        <Badge className="bg-rose-50 text-rose-700 border border-rose-200 uppercase tracking-wide text-[10px]">
                          {t.booking_status === 'no_show' ? 'no show' : t.booking_status}
                        </Badge>
                        {t.assignment_type && t.assignment_type !== 'primary' && (
                          <span className="text-[10px] uppercase text-gray-400">{t.assignment_type}</span>
                        )}
                        <span className="ml-auto text-[10px] text-gray-400">Excluded from payout</span>
                        {t.booking_status === 'no_show' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                            onClick={async () => {
                              const reason = window.prompt(
                                'Reason for restoring ' + (t.name || t.email) + ' (min 5 chars):',
                                'Talent did arrive — marked no-show by mistake',
                              );
                              if (!reason || reason.trim().length < 5) return;
                              try {
                                await api.post(`/admin/bookings/${t.booking_id}/restore-from-no-show`, {
                                  reason: reason.trim(),
                                  reverse_reliability: true,
                                });
                                toast.success('Talent restored. Booking is now confirmed again.');
                                load();
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Failed to restore');
                              }
                            }}
                            data-testid={`restore-no-show-btn-${t.booking_id}`}
                          >
                            Restore
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* 2026-06 — Permanent roster record. Always-available audit of who was
            assigned and who worked, with attendance + payment status, so completed
            events never lose their history (e.g. Duluth Home Show). */}
        {assigned.length > 0 && (() => {
          const shiftStatus = (data?.shift?.status || data?.status || '').toLowerCase();
          const shiftDate = data?.shift?.shift_date || data?.shift_date;
          const isPast = shiftDate ? (new Date(shiftDate) < new Date(new Date().toDateString())) : false;
          const isHistory = isPast || ['completed', 'closed', 'cancelled', 'archived'].includes(shiftStatus)
            || assigned.some(t => ['completed', 'timesheet_approved', 'paid', 'no_show'].includes(t.booking_status));
          if (!isHistory) return null;
          const payBadge = (t) => {
            const s = t.payable_status;
            if (s === 'paid') return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Paid</Badge>;
            if (s === 'ready') return <Badge className="bg-cyan-50 text-cyan-700 border border-cyan-200 text-[10px]">Ready to pay</Badge>;
            if (s === 'needs_review') return <Badge className="bg-amber-50 text-amber-700 border border-amber-200 text-[10px]">Needs review</Badge>;
            return <Badge className="bg-gray-100 text-gray-500 border border-gray-200 text-[10px]">—</Badge>;
          };
          const attBadge = (st) => {
            const map = {
              completed: ['Worked', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
              timesheet_approved: ['Worked', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
              paid: ['Worked', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
              checked_in: ['Checked in', 'bg-cyan-50 text-cyan-700 border-cyan-200'],
              no_show: ['No-show', 'bg-rose-50 text-rose-700 border-rose-200'],
              cancelled: ['Cancelled', 'bg-rose-50 text-rose-700 border-rose-200'],
              confirmed: ['Assigned', 'bg-slate-50 text-slate-600 border-slate-200'],
            };
            const [label, cls] = map[st] || [st, 'bg-slate-50 text-slate-600 border-slate-200'];
            return <Badge className={`${cls} border text-[10px] uppercase tracking-wide`}>{label}</Badge>;
          };
          return (
            <Card data-testid="roster-record-panel">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-indigo-500" />
                  Roster record <span className="text-xs font-normal text-gray-400">(permanent history)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b bg-gray-50 text-xs">
                      <th className="py-2 px-3 font-medium">Talent</th>
                      <th className="py-2 px-3 font-medium">Role</th>
                      <th className="py-2 px-3 font-medium">Attendance</th>
                      <th className="py-2 px-3 font-medium text-right">Hours</th>
                      <th className="py-2 px-3 font-medium text-right">Pay</th>
                      <th className="py-2 px-3 font-medium">Payment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assigned.map((t) => (
                      <tr key={t.booking_id} className="border-b last:border-0" data-testid={`roster-record-row-${t.booking_id}`}>
                        <td className="py-2 px-3 font-medium text-gray-900">{t.name || t.email}</td>
                        <td className="py-2 px-3 text-gray-500 text-xs">{t.assignment_type || 'primary'}{t.is_event_lead ? ' · lead' : ''}</td>
                        <td className="py-2 px-3">{attBadge(t.booking_status)}</td>
                        <td className="py-2 px-3 text-right">{t.hours_worked != null ? `${t.hours_worked}h` : '—'}</td>
                        <td className="py-2 px-3 text-right">{t.total_pay != null ? `$${Number(t.total_pay).toFixed(2)}` : '—'}</td>
                        <td className="py-2 px-3">{payBadge(t)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          );
        })()}

        {/* Manual Assign */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-cyan-500" />
                Manually Assign Talent
              </CardTitle>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setFindRepOpen(true)}
                className="border-cyan-300 text-cyan-700 hover:bg-cyan-50"
                data-testid="find-replacements-btn"
                title="Open Smart Replacement — race-to-claim notifications"
              >
                <Zap className="w-4 h-4 mr-1" />
                Find Replacements
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                value={talentQuery}
                onChange={(e) => setTalentQuery(e.target.value)}
                placeholder="Search talent by name, email, or city"
                className="pl-9"
                data-testid="talent-search-input"
              />
            </div>
            {talentSearching && <p className="text-sm text-gray-400">Searching…</p>}
            {!talentSearching && talentQuery.trim() && talentResults.length === 0 && (
              <p className="text-sm text-gray-400">No talent found.</p>
            )}
            {talentResults.length > 0 && (
              <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
                {talentResults.map((t) => {
                  const busyPrimary = assigning === `${t.id}:primary`;
                  const busyBackup = assigning === `${t.id}:backup`;
                  const busyAlternate = assigning === `${t.id}:alternate`;
                  const busyExtra = assigning === `${t.id}:extra`;
                  const primaryFull = (shift.staffed_count ?? shift.confirmed_count ?? 0) >= (shift.talent_needed ?? 0);
                  return (
                    <div key={t.id} className="px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="talent-result-row">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{(t.first_name || '') + ' ' + (t.last_name || '')}</div>
                        <div className="text-xs text-gray-500 truncate">{t.email}{t.city ? ` · ${t.city}, ${t.state || ''}` : ''}</div>
                        {(t.markets?.length > 0 || t.primary_market) && (
                          <div className="mt-1">
                            <MarketBadge
                              markets={t.markets || []}
                              primaryMarket={t.primary_market}
                              testId={`talent-result-market-${t.id}`}
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-gray-500">$</span>
                          <Input
                            type="number" step="0.01"
                            value={assignRates[t.id] ?? ''}
                            onChange={(e) => setAssignRates(prev => ({ ...prev, [t.id]: e.target.value }))}
                            placeholder={shift.talent_pay_rate ? String(shift.talent_pay_rate) : 'rate'}
                            className="h-8 w-20 text-xs"
                            data-testid={`assign-rate-input-${t.id}`}
                            title={
                              shift.talent_pay_rate_min != null && shift.talent_pay_rate_max != null
                                ? `Range $${shift.talent_pay_rate_min}–$${shift.talent_pay_rate_max} · default $${shift.talent_pay_rate}`
                                : `Default $${shift.talent_pay_rate ?? '—'}/hr`
                            }
                          />
                          <span className="text-[11px] text-gray-500">/hr</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => {
                            if (primaryFull) {
                              toast.error('Primary slots are full. Use Backup, Alternate, or Extra — or remove a current assignee first.');
                              return;
                            }
                            assign(t.id, 'primary');
                          }}
                          disabled={busyPrimary}
                          className={`${primaryFull ? 'bg-gray-300 hover:bg-gray-400 text-gray-700' : 'bg-cyan-500 hover:bg-cyan-600 text-white'}`}
                          title={primaryFull ? 'Primary slots full — tap to see options' : 'Assign to a primary slot'}
                          data-testid="assign-talent-btn"
                        >
                          {busyPrimary ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" />Assign</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => assign(t.id, 'backup')}
                          disabled={busyBackup}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          title="Assign as backup — counted against backups_needed"
                          data-testid="assign-backup-btn"
                        >
                          {busyBackup ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Backup</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => assign(t.id, 'alternate')}
                          disabled={busyAlternate}
                          className="border-violet-300 text-violet-700 hover:bg-violet-50"
                          title="Assign as alternate — pre-committed replacement, activated ad-hoc when a primary drops out. Not counted against staffing totals."
                          data-testid="assign-alternate-btn"
                        >
                          {busyAlternate ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Alternate</>}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => assign(t.id, 'extra')}
                          disabled={busyExtra}
                          className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                          title="Add extra/floater staff — uncounted overflow"
                          data-testid="assign-extra-btn"
                        >
                          {busyExtra ? <Loader2 className="w-4 h-4 animate-spin" /> : <>+ Extra</>}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Iter-186 — Recent invite activity + 1-tap retry-failed */}
        <RecentInviteActivityCard shiftId={shiftId} />

        {/* Iter-187 — Previous workers (per-client + per-role/venue) for fast urgent staffing. */}
        <PreviousWorkersSection
          shiftId={shiftId}
          clientId={shift.client_id}
          roleId={shift.role_id}
          venueName={shift.venue_name}
          roleName={shift.role_name}
        />
      </div>

      <InviteTalentModal
        open={inviteOpen}
        onOpenChange={(o) => { setInviteOpen(o); if (!o) setInviteNearbyRadius(null); }}
        shiftId={shiftId}
        shiftMeta={{ city: shift.city, state: shift.state, role_name: shift.role_name, market_id: shift.market_id }}
        autoNearbyRadius={inviteNearbyRadius}
      />

      <RecipientsModal
        open={recipientsOpen}
        onOpenChange={setRecipientsOpen}
        shiftId={shiftId}
        shiftLabel={`${shift.venue_name || shift.role_name} · ${shift.city}, ${shift.state}`}
      />

      {/* Iter-133w — Promote Backup → Primary dialog (audited) */}
      {/* AI Requirement Organizer — review & approve */}
      <Dialog open={!!aiOrg} onOpenChange={(o) => !o && setAiOrg(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="ai-organizer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-fuchsia-600" /> Organize requirements with AI
            </DialogTitle>
            <DialogDescription>
              AI reorganized this job's existing text into sections. It never invents requirements — review,
              edit, and approve. The original job description stays intact for talent.
            </DialogDescription>
          </DialogHeader>
          {aiOrg?.loading ? (
            <div className="py-12 text-center text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-fuchsia-600" /> Organizing…
            </div>
          ) : aiOrg?.sections ? (
            <div className="space-y-3">
              {Object.keys(AI_LABELS).map((k) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-slate-600">{AI_LABELS[k]}</label>
                  {k === 'task_checklist' ? (
                    <Textarea
                      value={(aiOrg.sections[k] || []).join('\n')}
                      onChange={(e) => setAiSection(k, e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))}
                      placeholder="One task per line (empty if none)"
                      className="text-sm" rows={3}
                      data-testid={`ai-section-${k}`}
                    />
                  ) : (
                    <Textarea
                      value={aiOrg.sections[k] || ''}
                      onChange={(e) => setAiSection(k, e.target.value)}
                      placeholder="Empty — AI found nothing for this section"
                      className="text-sm" rows={2}
                      data-testid={`ai-section-${k}`}
                    />
                  )}
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={discardAI} data-testid="ai-discard-btn">Discard</Button>
            <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={approveAI}
                    disabled={aiOrg?.loading} data-testid="ai-approve-btn">
              Approve &amp; show to talent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <DialogContent data-testid="promote-to-primary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Shield className="w-5 h-5" /> Promote backup to Primary
            </DialogTitle>
            <DialogDescription>
              Activating <strong>{promoteTarget?.name}</strong> as Primary Talent.
              Assignment type updates everywhere, invoice/payout recalculates using
              the full primary rate, the talent and client are notified of the staffing
              change, and the action is logged with your admin ID.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Reason (required, min 5 chars)</label>
            <Input
              value={promoteReason}
              onChange={(e) => setPromoteReason(e.target.value)}
              placeholder="Primary was a no-show — backup worked the shift"
              maxLength={500}
              data-testid="promote-reason-input"
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setPromoteTarget(null)} disabled={promoting} data-testid="promote-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  const reason = (promoteReason || '').trim();
                  if (reason.length < 5) { toast.error('Reason must be at least 5 characters'); return; }
                  setPromoting(true);
                  try {
                    await api.post(`/admin/bookings/${promoteTarget.booking_id}/promote-to-primary`, {
                      reason,
                      dropped_booking_id: droppedPrimaryForActivation?.booking_id || null,
                      dropout_reason: droppedPrimaryForActivation?.reason || null,
                    });
                    toast.success(`${promoteTarget.name} activated as Primary. Talent + client notified; invoice + payout updated.`);
                    setPromoteTarget(null);
                    await load();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Promotion failed');
                  } finally { setPromoting(false); }
                }}
                disabled={promoting || (promoteReason || '').trim().length < 5}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="promote-submit-btn"
              >
                {promoting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Shield className="w-4 h-4 mr-1" />}
                Confirm promotion
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit individual talent shift time (per-worker override) */}
      <Dialog open={!!timeEdit} onOpenChange={(o) => !o && setTimeEdit(null)}>
        <DialogContent data-testid="edit-time-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-700">
              <Clock className="w-4 h-4" /> Edit Individual Shift Time
            </DialogTitle>
            <DialogDescription>
              Set a start/end time for <strong>{timeEdit?.name}</strong> only. This becomes their
              effective time everywhere (reminders, countdown, clock-in). The master shift is unchanged.
            </DialogDescription>
          </DialogHeader>
          {timeEdit && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-600">Start time</label>
                  <input type="time" value={timeEdit.start_time}
                    onChange={(e) => setTimeEdit({ ...timeEdit, start_time: e.target.value })}
                    data-testid="edit-time-start" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-600">End time</label>
                  <input type="time" value={timeEdit.end_time}
                    onChange={(e) => setTimeEdit({ ...timeEdit, end_time: e.target.value })}
                    data-testid="edit-time-end" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-600">Note to talent (optional)</label>
                <input type="text" value={timeEdit.note}
                  onChange={(e) => setTimeEdit({ ...timeEdit, note: e.target.value })}
                  placeholder="e.g. Please report at 11 for a briefing"
                  data-testid="edit-time-note" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimeEdit(null)}>Cancel</Button>
            <Button onClick={saveTimeOverride} disabled={savingTime}
              className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="edit-time-save">
              {savingTime ? 'Saving…' : 'Save & Notify Talent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role dialog (Ops Queue #2) */}
      <Dialog open={!!changeRole} onOpenChange={(o) => !o && setChangeRole(null)}>
        <DialogContent data-testid="change-role-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <ArrowRightLeft className="w-4 h-4" /> Change Role
            </DialogTitle>
            <DialogDescription>
              Move <strong>{changeRole?.name}</strong> to a different role on this event. We move
              their booking to the correct role-shift (creating it if it doesn't exist yet) and
              notify the talent. Current role: <strong>{changeRole?.current_role || '—'}</strong>.
            </DialogDescription>
          </DialogHeader>
          {changeRole && (
            <div className="space-y-4">
              <TierRoleCombobox
                value={crRoleId}
                context="client_booking"
                testid="change-role-picker"
                onChange={(roleId) => onCrRolePick(roleId)}
              />
              {crPreview && (
                <div className="rounded-lg border border-indigo-200 bg-indigo-50/60 p-3 space-y-1.5 text-sm"
                  data-testid="change-role-preview">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Target role</span>
                    <span className="font-semibold text-gray-900">{crPreview.role_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Applicable pay rate</span>
                    <span className="font-semibold text-gray-900">
                      {crPreview.applicable_pay_rate != null ? `$${crPreview.applicable_pay_rate.toFixed(2)}/hr` : '—'}
                    </span>
                  </div>
                  {crPreview.current_pay_rate != null && (
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Current rate</span>
                      <span>${crPreview.current_pay_rate.toFixed(2)}/hr</span>
                    </div>
                  )}
                  {crPreview.will_create_shift && (
                    <div className="flex items-center gap-1.5 text-xs text-amber-700 pt-1">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      No existing shift for this role on this event — a new role-shift will be created.
                    </div>
                  )}
                </div>
              )}
              {crRoleId && (
                <>
                  <div>
                    <label className="text-xs text-gray-600">Pay rate override (optional)</label>
                    <input type="number" step="0.01" min="0" value={crRate}
                      onChange={(e) => setCrRate(e.target.value)}
                      placeholder="Defaults to the role's applicable rate"
                      data-testid="change-role-rate"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-600">Reason / note (optional, audited)</label>
                    <input type="text" value={crReason}
                      onChange={(e) => setCrReason(e.target.value)}
                      placeholder="e.g. Reassigned to Bartender per client request"
                      data-testid="change-role-reason"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangeRole(null)} data-testid="change-role-cancel">Cancel</Button>
            <Button onClick={confirmChangeRole} disabled={!crRoleId || crBusy}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="change-role-confirm">
              {crBusy ? 'Changing…' : 'Confirm & Notify Talent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Remove Talent with cancellation reason */}
      <Dialog open={!!removeTalent} onOpenChange={(o) => !o && setRemoveTalent(null)}>  <DialogContent data-testid="remove-talent-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <UserMinus className="w-5 h-5" /> Remove {removeTalent?.name}
            </DialogTitle>
          <DialogDescription className="sr-only">Remove</DialogDescription>
          </DialogHeader>
          {removeTalent && (
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">Reason</Label>
                <div className="space-y-2">
                  {REMOVAL_REASONS.map((r) => (
                    <label
                      key={r.value}
                      className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${
                        removeTalent.reason === r.value
                          ? 'border-red-400 bg-red-50'
                          : 'border-gray-200 hover:border-red-200 bg-white'
                      }`}
                      data-testid={`removal-reason-${r.value}`}
                    >
                      <input
                        type="radio"
                        name="removal-reason"
                        value={r.value}
                        checked={removeTalent.reason === r.value}
                        onChange={() => setRemoveTalent({ ...removeTalent, reason: r.value })}
                        className="mt-0.5"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm">{r.label}</div>
                        <div className="text-xs text-gray-500">{r.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-sm font-semibold text-gray-700">Notes (optional)</Label>
                <Textarea
                  rows={2}
                  value={removeTalent.notes}
                  onChange={(e) => setRemoveTalent({ ...removeTalent, notes: e.target.value })}
                  placeholder="Add context — sent to activity log"
                  data-testid="removal-notes-input"
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                <span>
                  This will update the talent's reliability score and reopen this shift's slot if
                  you drop below the staffing target. The action is logged.
                </span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTalent(null)} disabled={removing}>
              Cancel
            </Button>
            <Button
              onClick={confirmRemoveTalent}
              disabled={removing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-remove-talent-btn"
            >
              {removing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserMinus className="w-4 h-4 mr-2" /> Remove Talent</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay rate edit dialog */}
      <FindReplacementsDialog
        open={findRepOpen}
        onOpenChange={setFindRepOpen}
        shiftId={shiftId}
        onAfterTrigger={load}
      />
      <Dialog open={!!rateEdit} onOpenChange={(o) => !o && setRateEdit(null)}>        <DialogContent data-testid="rate-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit pay rate · {rateEdit?.name}</DialogTitle>
            <DialogDescription>
              {rateEdit?.locked
                ? 'This booking is rate-locked because the talent has accepted or is on-shift. Override requires a reason; the change will be audit-logged.'
                : 'Set a per-talent rate for this booking. The change will be audit-logged.'}
            </DialogDescription>
          </DialogHeader>
          {rateEdit && (
            <div className="space-y-3 py-2">
              <div className="text-xs text-gray-600">
                Bill rate: <span className="font-semibold">${rateEdit.bill_rate?.toFixed?.(2) ?? '—'}</span>
                {(rateEdit.min != null || rateEdit.max != null) && (
                  <span className="ml-3">Range: <span className="font-semibold">${rateEdit.min ?? '—'} – ${rateEdit.max ?? '—'}</span></span>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-sm">New rate ($/hr)</Label>
                <Input
                  type="number" step="0.01"
                  value={rateEdit.current_rate}
                  onChange={(e) => setRateEdit({ ...rateEdit, current_rate: e.target.value })}
                  data-testid="rate-edit-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Reason (required for audit)</Label>
                <Textarea
                  rows={2}
                  value={rateEdit.reason || ''}
                  onChange={(e) => setRateEdit({ ...rateEdit, reason: e.target.value })}
                  placeholder="e.g. 5+ years experience, premium client"
                  data-testid="rate-edit-reason"
                />
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={!!rateEdit.force_override}
                  onChange={(e) => setRateEdit({ ...rateEdit, force_override: e.target.checked })}
                  data-testid="rate-edit-force"
                />
                Force override (required if locked or outside range)
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateEdit(null)} disabled={rateSaving}>
              Cancel
            </Button>
            <Button
              onClick={submitRateEdit}
              disabled={rateSaving}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
              data-testid="rate-edit-save-btn"
            >
              {rateSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save rate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assigned-talent change confirmation */}
      <AlertDialog open={editConfirmOpen} onOpenChange={setEditConfirmOpen}>
        <AlertDialogContent data-testid="edit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Talent are already assigned to this shift
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are changing <strong>major shift details</strong> (date, time, venue, or rate).
                Any assigned talent will need to re-confirm the updated booking.
              </span>
              <span className="block text-xs text-gray-600">
                Proceed only if you intend to push this correction to the assigned staff.
                You may want to message them directly after saving.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={save}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-edit-shift-btn"
            >
              Save changes anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-shift-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Move this shift to Trash?</AlertDialogTitle>
            <AlertDialogDescription>
              The shift will be archived (not permanently deleted). It disappears from the
              active schedule and invoice list. A Super Admin can restore it from the Trash
              Bin, or hard-delete it later if it was truly junk.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSoftDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-shift-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hard Delete (Super Admin only) — type "DELETE" to confirm */}
      <AlertDialog open={hardDeleteOpen} onOpenChange={setHardDeleteOpen}>
        <AlertDialogContent data-testid="hard-delete-shift-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Permanently delete this shift?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                This erases the gig <strong>and all its related data</strong> (bookings,
                invitations, applications, scheduled reminders and notifications). It will
                no longer appear in talent search, invitations or active job listings.
                This cannot be undone.
              </span>
              {deleteImpact && (
                <span className="block text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900" data-testid="delete-impact-summary">
                  <span className="block font-semibold">What this affects:</span>
                  <span className="block">{deleteImpact.counts?.confirmed_bookings || 0} confirmed booking(s) · {deleteImpact.counts?.bookings || 0} total booking(s)</span>
                  <span className="block">{deleteImpact.counts?.applications || 0} application(s) · {deleteImpact.counts?.invitations || 0} invitation(s)</span>
                  {(deleteImpact.blockers || []).length > 0 && (
                    <span className="block mt-1 font-semibold text-red-700">
                      Blocked: {deleteImpact.blockers.join('; ')}. Cancel or archive this gig instead.
                    </span>
                  )}
                </span>
              )}
              <span className="block text-xs text-gray-500">
                If the gig is tied to a paid invoice or other locked record, the system will
                automatically fall back to archiving it instead.
              </span>
              <span className="block mt-3 text-sm font-medium text-gray-800">
                Type <span className="font-mono text-red-700">DELETE</span> to confirm:
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1">
            <Input
              value={hardDeleteConfirm}
              onChange={(e) => setHardDeleteConfirm(e.target.value)}
              placeholder="Type DELETE"
              className="border-red-300 focus-visible:ring-red-500"
              data-testid="hard-delete-confirm-input"
              disabled={hardDeleting}
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={hardDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleHardDelete}
              disabled={hardDeleting || hardDeleteConfirm !== 'DELETE'}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
              data-testid="confirm-hard-delete-shift-btn"
            >
              {hardDeleting
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <AlertTriangle className="w-4 h-4 mr-2" />}
              Permanently Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* GPS breadcrumb viewer */}
      <BreadcrumbViewer
        bookingId={locBookingId}
        open={!!locBookingId}
        onOpenChange={(open) => { if (!open) setLocBookingId(null); }}
      />

      {/* Iter-132: Applicant drawer — right-side panel with Quick Book */}
      {/* Iter-200: wrapped in ErrorBoundary so a single bad applicant row
          never white-screens the admin shift detail page. */}
      <ErrorBoundary
        renderFallback={(reset) => (
          <DrawerInlineError
            reset={reset}
            onClose={() => { reset(); setApplicantDrawerOpen(false); }}
          />
        )}
      >
        <ApplicantDrawer
          shiftId={shiftId}
          open={applicantDrawerOpen}
          onClose={() => setApplicantDrawerOpen(false)}
          onBooked={load}
        />
      </ErrorBoundary>

      {/* Iter-152: Manual shift reminder modal */}
      <SendShiftReminderModal
        open={reminderModalOpen}
        onClose={() => setReminderModalOpen(false)}
        shift={{
          id: shiftId,
          venue_name: shift?.venue_name,
          shift_date: shift?.shift_date,
          start_time: shift?.start_time,
        }}
      />

      <SendEventEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        shift={{ id: shiftId, venue_name: shift?.venue_name }}
      />

      {/* Iter-411p: Multi-Day Event schedule editor (add/remove/edit dates)
          restored to Edit Shift flow. Uses existing
          /api/admin/multi-slot-events endpoints — auto-promotes a solo
          shift to a series on first "Add date" so shared fields (client,
          event, venue, position, briefing) don't need to be re-entered. */}
      {eventScheduleOpen && (
        <Dialog open={eventScheduleOpen} onOpenChange={setEventScheduleOpen}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Manage Event Dates</DialogTitle>
              <DialogDescription>
                Add, remove, or edit event dates. Shared client, venue, event name,
                position, and instructions carry across all dates.
              </DialogDescription>
            </DialogHeader>
            <EventScheduleEditor
              shiftId={shiftId}
              onClose={() => setEventScheduleOpen(false)}
              onSaved={async () => { setEventScheduleOpen(false); await load(); }}
            />
          </DialogContent>
        </Dialog>
      )}
      <FlagViolationModal
        open={!!flagTarget}
        talentId={flagTarget?.talentId}
        talentName={flagTarget?.talentName}
        bookingId={flagTarget?.bookingId}
        shiftId={flagTarget?.shiftId}
        onClose={() => setFlagTarget(null)}
        onFlagged={() => {}}
      />
    </div>
  );
};

export default AdminShiftDetail;
